/**
 * Task Display Configuration — Agent & Recommendation Authority
 * Phase 9a.2: Intent-driven task rendering config.
 *
 * Authority: UI_DESIGN_CONTRACT_V3.md (Section B)
 * This file governs how agent tasks appear in the inbox, detail views, and agent pages.
 * Zero inline hex codes — all visual properties trace to DESIGN_INTENTS in ui-invariants.js.
 */

import {
  DEAL_SCREEN_CARD,
  KPI_TILE,
  SECTION_PANEL,
  DS_COLORS,
} from './ui-invariants';

// ============================================================
// AGENT CONFIGURATION
// Each agent registered in the system has actions, detail sections, and input config.
// ============================================================

export const AGENT_CONFIG = {
    sector_intelligence: {
        id: 'sector_intelligence',
        label: 'Sector Intelligence',
        description: 'Analyzes articles and research through 7 analytical lenses to surface investment-relevant insights.',
        icon: 'MagnifyingGlass',
        path: '/agents/sector-intelligence',
        designVersion: 'v0',
        group: 'analysis',

        actions: [
            {
                id: 'accept',
                label: 'Save to Knowledge Base',
                intent: 'primary',
                dropdown: {
                    type: 'calibration',
                    items: [
                        { id: 'agreed', label: 'Agent got it right' },
                        { id: 'rated_higher', label: 'I rate higher' },
                        { id: 'rated_lower', label: 'I rate lower' },
                    ],
                },
            },
            {
                id: 'reject',
                label: 'Dismiss',
                intent: 'negative',
                dropdown: {
                    type: 'rejection_reasons',
                    items: [
                        { id: 'not_relevant', label: 'Not relevant to fund thesis' },
                        { id: 'low_quality', label: 'Analysis too shallow' },
                        { id: 'duplicate', label: 'Duplicate or overlapping' },
                        { id: 'weak_angle', label: 'Weak contrarian angle' },
                        { id: 'press_release', label: 'Low signal — press release' },
                    ],
                },
            },
            {
                id: 'override',
                label: 'Override',
                intent: 'neutral',
                dropdown: {
                    type: 'override_reasons',
                    items: [
                        { id: 'cross_sector', label: 'Cross-sector relevance' },
                        { id: 'advisory_value', label: 'Advisory or mentorship value' },
                        { id: 'thesis_signal', label: 'Emerging thesis signal' },
                        { id: 'rare_data', label: 'Contains rare data or metrics' },
                    ],
                },
            },
        ],

        detailSections: [
            { id: 'summary', label: 'Analysis Summary', type: 'rich_text' },
            { id: 'beliefTags', label: 'Belief Alignment', type: 'tag_list', intentMap: 'beliefType' },
            { id: 'analyticalLenses', label: 'Analytical Lenses', type: 'accordion', count: 7 },
            { id: 'sourceInfo', label: 'Source Information', type: 'metadata' },
        ],

        inputConfig: {
            urlField: { enabled: true, label: 'Article URL', placeholder: 'Paste article URL...' },
            textPaste: { enabled: true, label: 'Or paste article text', placeholder: 'Paste full article text...' },
            emailCheck: { enabled: true, label: 'Check Email for Articles' },
        },
    },

    post_studio: {
        id: 'post_studio',
        label: 'Post Studio',
        description: 'Generates investor-grade LinkedIn posts from Knowledge Base analysis for CEO review.',
        icon: 'PencilLine',
        path: '/agents/linkedin-post',
        designVersion: 'v0',
        group: 'content',

        actions: [
            {
                id: 'reject',
                label: 'Reject',
                intent: 'negative',
                position: 'left',
                dropdown: {
                    type: 'rejection_reasons',
                    // reasons now fetched at runtime from GET /api/linkedin-posts/reject-reasons
                    items: [],
                },
            },
            {
                id: 'save_draft',
                label: 'Save Draft',
                intent: 'neutral',
                position: 'middle',
            },
            {
                id: 'post',
                label: 'Post',
                intent: 'primary',
                position: 'right',
            },
            {
                id: 'override',
                label: 'Reconsider',
                intent: 'neutral',
                position: 'left',
            },
        ],

        labels: {
            // PENDING IS ABOUT DRAFTS, NOT IDEAS (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001
            // Leg 1, CEO ruling recorded at P-124).
            //
            // This read "No post ideas waiting — the agent will surface new ideas after articles
            // are saved to the Knowledge Base." That is the IDEAS tab's sentence, and it was
            // sitting on the tab about DRAFTS. The tab semantics the CEO ruled are: IDEAS is
            // judgement on an IDEA, PENDING is judgement on a DRAFT. An empty Pending tab means
            // no finished draft is waiting for his review — it says nothing about whether ideas
            // exist, and it must not send him to the Knowledge Base to fix a drafting queue.
            //
            // It also names where a draft comes FROM, because that is the one thing about this
            // tab that legitimately changed: Pending is now sourced from ideas that were approved
            // and then drafted. Saying so is what makes an empty tab actionable rather than inert.
            pendingEmpty: 'No drafts waiting for your review. A draft appears here after you approve an idea and the agent writes it up.',
            savedEmpty: 'No saved drafts yet.',
            unresolvedNotesTitle: 'Still open on this draft',
            postedEmpty: 'You have not marked any post as published yet. Your history will appear here.',
            rejectedEmpty: 'No rejected posts.',
            rejectSuccess: 'Post rejected.',
            rejectError: 'Could not reject — please try again.',
            agentRecommendsSave: 'Agent recommends posting',
            agentRecommendsReject: 'Agent recommends skipping',
            // PILL WORDING (WI-ONE-CARD-EVERY-TAB-COPY-SAVED-AND-DELETE-THE-COPIES-001).
            // These were typed into the card component. A card shared by five tabs cannot
            // carry its own words, so they are given a home here like every other label.
            scorePill: (n) => `Score ${n}`,
            scorePillTooltip: 'Ranker score (0–100)',
            contentTypeAria: (label) => `Content type: ${label}`,
            saveSuccess: 'Draft saved.',
            saveError: 'Could not save draft.',
            // WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 (CEO ruling 2026-07-17). Was
            // "Posted \u2014 it's live.", which was never true: this system has no LinkedIn
            // integration and never posts. Post is the CEO's BOOKKEEPING tap \u2014 he publishes
            // by hand, then records it here. The copy now says what actually happened.
            // {tab} is substituted at render time from the tabs config below, so the
            // announcement can never name a tab that isn't on screen. (The CEO's ruling
            // asked this to say "Published tab"; there is no such tab \u2014 it is "Posted".)
            postSuccess: 'Marked as published and moved to your {tab} tab.',
            postError: 'Could not post \u2014 please try again.',
            unmarkSuccess: 'Moved back to Saved \u2014 no longer marked as published.',
            unmarkError: 'Could not undo \u2014 please try again.',
            // WI-IDEA-FULL-READ-AND-CONFIRM-IN-FLIGHT-001 Leg 2. The BUTTON's own words. The
            // outcome messages above were declared here from the start, but the label the CEO
            // actually reads and hears was a literal in LinkedInPostAgent.jsx behind a
            // `PS_LABELS.unmarkLabel || '...'` fallback \u2014 a key no config ever set, so the
            // fallback WAS the product. Found while lifting this control out of the collapsing
            // region: the test asserting "every value traces to a config key" failed on it.
            // Named "Unmark as published" rather than "Undo" because a screen-reader user
            // tabbing the Posted tab hears the button out of context, and "Undo" alone does not
            // say what it undoes.
            unmarkLabel: 'Unmark as published',
            unmarkBusy: 'Undoing\u2026',
            overrideSuccess: 'Moved back to Saved.',
            overrideError: 'Could not override \u2014 please try again.',
            // WI-LINKEDIN-POST-FRONTEND-V1 \u2014 per-card Ranker thumbs feedback.
            rankerFeedbackPrompt: 'Was this rank right?',
            rankerFeedbackThumbsUpAria: 'Rank was right',
            rankerFeedbackThumbsDownAria: 'Rank was wrong',
            rankerFeedbackUpSuccess: 'Thanks \u2014 rank looked right',
            rankerFeedbackDownSuccess: 'Thanks \u2014 Ranker will recalibrate',
            rankerFeedbackError: 'Could not record that \u2014 please try again.',
            postedMetricsCapturePrompt: 'No metrics captured yet. Send "metrics for {topic} text" to the Telegram bot to log them.',
            // WI-LINKEDIN-SAVE-BUTTON-LIVE-FAILURE-001 — screen-reader copy for
            // the tablist label and the polite live-region action announcements.
            tablistLabel: 'Post Studio views',
            announceOverride: 'Post moved to Saved. Now showing your Saved posts.',
            announceSaved: 'Post saved. Now showing your Saved posts.',
            // WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 (CEO ruling 2026-07-17). THIS is the
            // line the CEO actually hears when he presses Post — the page-level transition
            // announcement, not the card's toast. It said "Post published", which was false:
            // this system has no LinkedIn integration and never posts. Post is bookkeeping —
            // he publishes by hand, then records it here. {tab} is substituted from the tabs
            // config at render time so the announcement can never name a tab that isn't on
            // screen. (The ruling asked for "your Published tab"; there is no such tab.)
            announcePosted: 'Marked as published and moved to your {tab} tab.',
            announceUnmarked: 'Moved back to Saved — no longer marked as published. Now showing your Saved posts.',
            announceRejected: 'Post rejected. Now showing your Rejected posts.',
            // WI-POST-STUDIO-FULL-OPERABILITY-001 — every card-local action now
            // routes its outcome through the shared aria-live announcer, on BOTH
            // success and error/paused paths (not just toast). Where the backend
            // supplies a real reason (err.message — e.g. the publishing-paused
            // detail on a 409), that honest reason is announced instead of these
            // generic fallbacks; these labels are used only when no backend
            // detail is available.
            copySuccess: '{label} copied.',
            copyError: 'Could not copy — try selecting manually.',
            editSaveSuccess: 'Edits saved.',
            editSaveError: 'Could not save edits — please try again.',
            calibrationSelected: 'Marked calibration: {label}.',
            rejectReasonMenuLabel: 'Reject reason',
            // WI-IDEA-CARD-BUILD-001 Leg 2 — pre-draft idea card actions. Additive
            // only; every existing key above is untouched.
            cardApproveSuccess: 'Idea approved.',
            cardApproveError: 'Could not approve this idea — please try again.',
            cardPromoteSuccess: 'Idea promoted to drafting.',
            cardPromoteError: 'Could not promote this idea — please try again.',
            cardReasonRequired: 'Pick a reason before promoting or rejecting this idea.',
            announceCardApproved: 'Idea approved.',
            announceCardRejected: 'Idea rejected — reason recorded.',
            announceCardPromoted: 'Idea promoted to drafting — reason recorded.',
            cardPromoteReasonMenuLabel: 'Promote reason',
            // WI-IDEA-CARD-BUILD-001 Leg 4 — the on-page idea-card gate toggle.
            // Business language only: the CEO reads "review every idea first",
            // never "gate mode" / "sampled" / "mandatory" (those are the config's
            // words, not his). Authority: IDEA_CARD_CONTRACT.md §2h.
            gateToggleLabel: 'Review every idea before drafting',
            gateToggleHint: 'When off, strong ideas go straight to drafting and cards are a sample.',
            gateToggleOnAnnounce: 'On. Every idea now waits for your decision before drafting.',
            gateToggleOffAnnounce: 'Off. Strong ideas go straight to drafting.',
            gateToggleLoadError: 'Could not load the idea review setting.',
            gateToggleSaveError: 'Could not change the setting — it is unchanged.',
            // WI-IDEA-CARD-BUILD-001 Leg 6 (§2e) — the visible produced-without-a-View
            // stamp. Fallback only: the backend sends the authoritative stamp text.
            cardNoViewStamp: 'Written without a ratified View',
            cardNearDuplicateWarning: 'Looks like an existing position — check before deciding.',
            // Idea-gate merit lines + the promote action label. Centralised here
            // (not inline in JSX) per the frontend hardcode-scan rule.
            ideaGateHeading: 'Why this clears the bar',
            ideaGateNonObvious: 'Non-obvious',
            ideaGateConsequence: 'Consequence (advisory, not yet agreed with the CEO)',
            ideaGateGrounded: 'Grounded in a real source',
            // A3 (CEO 2026-07-19): the field reads as investor language. "Falsifiable" is a
            // philosopher's word for what an investor calls knowing what would change your mind
            // — and the line beneath it IS that test, so the label names the thing directly.
            ideaGateFalsifiable: 'What would change this view',
            cardPromoteAction: 'Promote to Draft',
            // WI-IDEA-CARD-ASSEMBLY-REDESIGN-001 Leg C — the ratified §2j render order:
            // Claim -> Case -> Bar -> Trigger block -> Receipt -> quiet no-View note ->
            // buttons. Centralised here (not inline in JSX) per the frontend
            // hardcode-scan rule.
            caseHeading: 'The case',
            barFailureFlagHeading: 'Flagged',
            barFailureFlagPrefix: 'Did not clear the bar:',
            triggerBlockHeading: 'What triggered this',
            dateNotCapturedLabel: 'Publication date not captured',
            receiptHeading: 'What we read',
            // A6 (Leg 5): the View check states which tags it searched, so "nothing carries
            // these tags" can be told apart from a lookup that never ran.
            viewLookupHeading: 'View check',
            // F1 (ruling 257): the firm's lane and timing rules applied to the card, each with an
            // honest "unknown" where the rule is not written.
            // F2 (ruling 257): where each case slot, the falsifier and why-it-won came from.
            selectionHeading: 'Where this came from',
            selectionRebuilt: "rebuilt from this card's stored analysis, not recorded when it was built",
            selectionNotRecorded: 'Where each part came from was not recorded: this card was built before cards kept that record.',
            selectionFieldWords: {
                accrezeo_analysis: 'depth analysis',
                counter_argument: 'counter-argument',
                cross_sector_parallels: 'cross-sector parallels',
            },
            laneTimingHeading: 'Lane and timing',
            laneTimingValueLabels: {
                in_lane: 'in our lane',
                out_of_lane: 'outside our lane',
                no_match: 'not placed',
                unknown: 'unknown',
                dated: 'dated',
            },
            // D-2026-07-20-13: the similarity fallback names itself on the receipt.
            receiptNearestUnrelated: 'nothing tagged to this subject — nearest material we hold, may be unrelated',
            // §2m: an empty case slot says so rather than being quietly filled from elsewhere.
            caseSlotEmpty: 'the article carried nothing for this',
            caseClaimsHeading: 'Where each point comes from',
            caseHoldsHeading: 'Why it holds',
            caseAgainstHeading: 'The strongest case against it',
            caseAgainstNone: 'No case against it was set out on this card.',
            takeawayNotChecked: 'This takeaway was not checked against other fields.',
            relationHeading: 'Compared with what we have published',
            takeawayHeading: 'The takeaway',
            takeawayGeneric: 'This takeaway could be said of any field, not only this one.',
            caseClaimNotBacked: 'Not yet backed by a passage in our material',
            // A4: a failed check with no stored reason says so, rather than rendering a bare "No".
            barFailureNoReason: 'no reason was recorded for this one',
            // §2m slot labels — what makes the sourcing legible to the reader.
            caseSlotPatternContext: 'Where else this has shown up',
            caseSlotConsequence: 'What follows if it holds',
            caseSlotCounterArgument: 'The strongest case against',
            caseCounterFromArticle: "this is the article's own counter-argument, not ours",
            // Human-readable names for the shelf-reader's reading-order categories
            // (backend/core/agents/linkedin_post/card_shelf_reader.py), used to render
            // the receipt line honestly — never a raw snake_case category key.
            // contrarian_analysis LEFT the shelf 2026-07-19 (CEO,
            // WI-IDEA-CARD-CONTRACT-AMENDMENT-001) — it is a mandatory composer
            // ingredient, not a shelf asset. Eight categories, not nine.
            receiptCategoryLabels: {
                large_view: 'View',
                source_article: 'the triggering article',
                similar_kb_entries: 'KB entry',
                published_positions: 'published position',
                deal_screens_in_sector: 'deal screen',
                ratified_lens_registry: 'lens registry entry',
                atomic_view: 'atomic view',
                cross_sector_pattern_work: 'cross-sector pattern work',
                sector_pages: 'our sector page',
                article_agent_output: 'part of our analysis of the article',
                past_ideas: 'earlier idea decision',
                published_posts: 'published post',
            },
            // Plurals are DECLARED, never constructed. A naive label+'s' rendered
            // "5 KB entrys" to the CEO (WI-IDEA-CARD-CONTRACT-AMENDMENT-001, A5) —
            // the -y/-ies class of English plural cannot be suffixed. Any label whose
            // plural is not label+'s' needs an entry here; the renderer falls back to
            // label+'s' only for the regular cases.
            receiptCategoryLabelsPlural: {
                large_view: 'Views',
                similar_kb_entries: 'KB entries',
                published_positions: 'published positions',
                deal_screens_in_sector: 'deal screens',
                ratified_lens_registry: 'lens registry entries',
                atomic_view: 'atomic views',
                cross_sector_pattern_work: 'cross-sector pattern work',
                sector_pages: 'our sector pages',
                article_agent_output: 'parts of our analysis of the article',
                past_ideas: 'earlier idea decisions',
                published_posts: 'published posts',
            },
            receiptSearchedOnTemplate: 'searched on {basis}',
            // A5 — the receipt names what it read. A count nobody can audit is not a
            // receipt; these render the entry titles and make the fetch cut visible.
            receiptTitlesJoiner: ' · ',
            receiptCutNoteTemplate: 'top {limit} of the closest matches',

            // ============================================================
            // WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 — "every card ends in a verb".
            // The server declares which decisions are legal on a card via
            // payload.available_verbs (a closed token set); the surface renders one
            // button per token and decides nothing itself. verbButtonLabels is the
            // ONLY place a verb token is turned into words a human reads — never a
            // raw token on screen (CLAUDE.md rules 17-18).
            // ============================================================
            verbButtonLabels: {
                approve: 'Approve',
                reject: 'Reject',
                promote: 'Promote to Draft',
                defer: 'Defer',
                acknowledge: 'Got it',
                repair: 'Fix this line',
                overrule: 'Keep it anyway',
                re_evaluate: 'Try again',
            },
            // Repair — the CEO edits the exact sentence a gate objected to.
            repairFieldLabel: 'Your replacement wording',
            repairSubmitLabel: 'Save and check again',
            repairSuccess: 'Fixed — checking it again.',
            repairError: 'Could not save that fix — please try again.',
            repairReplacementRequired: 'Write a replacement before saving.',
            // Overrule — the CEO keeps the line as written; the objection stays visible.
            overruleFieldLabel: 'Say why, if you want to (optional)',
            overruleSubmitLabel: 'Keep it anyway',
            overruleSuccess: 'Kept as written — your objection stays on record below.',
            overruleError: 'Could not record that — please try again.',
            overruledNote: 'You chose to keep this as written.',
            // Re-evaluate — a bare retry, or the automatic follow-up to a repair.
            reEvaluateSuccess: 'Checked again.',
            reEvaluateError: 'Could not check again — please try again.',
            reEvaluateRetryCount: (n) => (n === 1 ? 'Tried 1 time.' : `Tried ${n} times.`),
            // The backend's `outcome` string is machine vocabulary (CLAUDE.md rules 17-18
            // forbid rendering it verbatim). A declared map, never a raw pass-through — an
            // outcome value not yet in this map renders unknownOutcome instead of the token.
            // REWRITTEN 2026-08-04 (WI-THE-IDEAS-TAB-TELLS-THE-TRUTH-001). This map used to
            // declare `passed` / `failed` / `recirculated` — and the server returns NONE of
            // those three. `auto_recirculated` against `recirculated` is the near-miss that
            // made the gap invisible to anyone scanning the file.
            //
            // The cost was measured: on 2026-08-04 the CEO pressed "Try again", the card WAS
            // recirculated, and every outcome fell through to `unknownOutcome` — so his
            // screen said "Checked again." beside a screen-reader-only "we got a result we
            // don't recognise yet". A success and a dead source article read identically.
            //
            // The keys below are pinned to the server's declared vocabulary
            // (`work_inbox.RE_EVALUATION_OUTCOME_TOKENS`) by
            // `backend/tests/test_the_click_can_be_spoken.py`, which fails if EITHER side
            // adds or drops one. `intent` drives the banner colour, so a refusal can never
            // be shown in the same tone as a success.
            reEvaluationOutcomeLabels: {
                auto_recirculated: { intent: 'positive', text: 'This idea has been sent back to be written again. A fresh card is waiting in your Ideas list.' },
                would_surface: { intent: 'positive', text: 'It clears the checks now, so it is back in front of you.' },
                await_ceo: { intent: 'warning', text: 'Nothing changed — this one is your decision to make, not something a retry can settle.' },
                already_recirculated: { intent: 'warning', text: 'This idea was already sent back once. The newer card is the live one.' },
                source_already_has_live_card: { intent: 'warning', text: 'Another idea from the same article is already live, so a second one was not created.' },
                'still withheld': { intent: 'warning', text: 'A check still objects to this one, so it has not moved.' },
                source_missing: { intent: 'negative', text: 'The article this idea came from is no longer available, so it cannot be written again.' },
            },
            // The enforceable guard (§3): a card with NO available verb at all is a
            // RENDER FAILURE, never a silent dead end. Loud, plain business language,
            // never a raw field name or taxonomy token.
            noAvailableVerbHeading: 'This card cannot tell us what you can do next',
            noAvailableVerbBody: 'Something went wrong preparing this card’s actions. We’ve flagged it — please come back to it, or refresh.',
            // An action outcome the frontend does not recognise. Never silently treated
            // as success and never a raw string dumped on screen.
            unknownOutcome: 'We got a result we don’t recognise yet — nothing was lost, but please check back on this card.',
        },

        calibrationOptions: [
            { id: 'agent_got_it_right', label: 'Agent got it right' },
            { id: 'rate_higher', label: 'I rate higher' },
            { id: 'rate_lower', label: 'I rate lower' },
        ],

        // ONE DOOR — the whole idea-and-draft flow lives on this page, as tabs in the order
        // the work actually moves: IDEA first, then PENDING, then SAVED. Posted and Rejected
        // keep their places after Saved. The idea surface was a standalone screen at /ideas
        // until 2026-07-30; a separate idea screen is exactly how five ideas sat nine days
        // unseen, so it was moved into the first tab and that route was removed — one route,
        // one component, one entry. 'idea' being first also makes it the tab a fresh visit
        // opens on, which is why STATE_EPOCH went 8 → 9.
        // WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001 (CEO ruling 2026-07-30).
        // `noun` is what the tab's count is COUNTING, spoken aloud by the screen reader.
        // Every tab used to announce "N posts", so the Ideas tab read out as "Ideas, 5 posts"
        // — five ideas, not five posts, and the CEO hears this surface rather than seeing it.
        // Singular form only; the renderer pluralises.
        // WI-IDEA-RERUN-AND-ACTIONABLE-IDEA-TAB-001 (CEO ruling 2026-07-30).
        tabs: [
            { id: 'idea',     label: 'Ideas',    defaultOpen: true,  noun: 'idea' },
            { id: 'pending',  label: 'Pending',  defaultOpen: true,  noun: 'draft' },
            { id: 'saved',    label: 'Saved',    defaultOpen: true,  noun: 'saved draft' },
            { id: 'posted',   label: 'Posted',   defaultOpen: false, noun: 'post' },
            { id: 'rejected', label: 'Rejected', defaultOpen: false, noun: 'rejected draft' },
        ],

        detailSections: [
            { id: 'postText', label: 'Post Draft', type: 'editable_text' },
            { id: 'firstComment', label: 'First Comment', type: 'copyable_text' },
            { id: 'associateBriefs', label: 'Agent Thinking', type: 'accordion', count: 3 },
            { id: 'sourceInfo', label: 'Source', type: 'metadata' },
        ],

        cardCollapsible: {
            defaultCollapsed: true,
            headerMinHeight: 44,
            caretIcon: 'CaretDown',
            expandedShadow: true,
        },

        regeneration: {
            modifiers: [],  // Phase 1: knobs disabled; REGEN_MODIFIERS.length > 0 guard hides the dial section
            states: { plus: 'plus', minus: 'minus', neutral: 'neutral' },
            buttonLabel: 'Regenerate Draft',
            loadingLabel: 'Refining your draft...',
            successMessage: 'Draft updated',
            gateFailedMessage: "Draft didn't pass quality review — try different settings",
            errorMessage: 'Something went wrong — please try again',
        },
    },

    founder_outreach: {
        id: 'founder_outreach',
        label: 'Founder Outreach',
        description: 'Generates personalized outreach emails to qualified founders for CEO approval before sending.',
        icon: 'EnvelopeSimple',
        path: '/agents/founder-outreach',
        designVersion: 'v0',
        group: 'relationships',

        actions: [
            {
                id: 'approve',
                label: 'Approve & Send',
                intent: 'primary',
                dropdown: null,
            },
            {
                id: 'reject',
                label: 'Reject Email',
                intent: 'negative',
                dropdown: {
                    type: 'rejection_reasons',
                    items: [
                        { id: 'not_relevant', label: 'Not relevant to fund thesis' },
                        { id: 'low_quality', label: 'Low quality / poorly written' },
                        { id: 'duplicate', label: 'Already covered / duplicate' },
                        { id: 'bad_timing', label: 'Timing not right' },
                        { id: 'needs_research', label: 'Needs more research first' },
                    ],
                },
            },
            {
                id: 'snooze',
                label: 'Snooze',
                intent: 'neutral',
                dropdown: {
                    type: 'defer_durations',
                    items: [
                        { id: '1d', label: 'Snooze — 1 day' },
                        { id: '1w', label: 'Snooze — 1 week' },
                        { id: '1m', label: 'Snooze — 1 month' },
                    ],
                },
            },
        ],

        detailSections: [
            { id: 'recipient', label: 'Recipient', type: 'founder_card' },
            { id: 'emailPreview', label: 'Email Preview', type: 'email_render' },
            { id: 'founderContext', label: 'Founder Context', type: 'metadata' },
            { id: 'campaignInfo', label: 'Campaign Details', type: 'metadata' },
        ],

        display: {
            showRecommendation: false,
        },

        inputConfig: {
            batchTrigger: { enabled: true, label: 'Generate Email Batch' },
            eligibilityPreview: { enabled: true, label: 'Preview Eligible Founders' },
        },
    },

    lead_gen_scout: {
        id: 'lead_gen_scout',
        label: 'LinkedIn Scout',
        description: 'Sends LinkedIn connection requests to cold leads from the Tracxn pool for CEO review.',
        icon: 'LinkedinLogo',
        path: '/agents/linkedin-scout',
        designVersion: 'v0',
        group: 'sourcing',

        // Config-driven workflow modes — labels swap when backend flips SCOUT_MANUAL_WORKFLOW_ENABLED.
        // ScoutTaskDetail reads workflow_labels from the export API at mount time.
        workflowModes: {
            manual: {
                primary_button_label: 'Approve & Download',
                primary_button_loading: 'Preparing Download...',
                confirm_title: 'Approve {count} {noun}?',
                confirm_description: 'This will download the approved candidates as an Excel file for manual outreach.',
                confirm_button: 'Approve & Download',
                toast_success: 'Batch approved and downloaded',
            },
            automated: {
                primary_button_label: 'Send Connection Requests',
                primary_button_loading: 'Sending...',
                confirm_title: 'Send {count} LinkedIn Connection {noun}?',
                confirm_description: 'This will queue {count} connection {noun} for your Chrome extension to send.',
                confirm_button: 'Confirm',
                toast_success: 'Connection requests queued',
            },
        },

        actions: [
            {
                id: 'approve',
                label: 'Approve & Download',
                intent: 'primary',
                dropdown: null,
            },
            {
                id: 'reject',
                label: 'Reject',
                intent: 'negative',
                dropdown: {
                    type: 'rejection_reasons',
                    items: [
                        { id: 'not_relevant', label: 'Not relevant to fund thesis' },
                        { id: 'wrong_stage', label: 'Wrong stage or sector' },
                        { id: 'already_connected', label: 'Already connected' },
                        { id: 'not_now', label: 'Not the right time' },
                    ],
                },
            },
            {
                id: 'snooze',
                label: 'Snooze',
                intent: 'neutral',
                dropdown: {
                    type: 'defer_durations',
                    items: [
                        { id: '1d', label: 'Snooze \u2014 1 day' },
                        { id: '1w', label: 'Snooze \u2014 1 week' },
                    ],
                },
            },
        ],

        detailSections: [
            { id: 'candidateList', label: 'Candidates', type: 'candidate_cards' },
            { id: 'connectionMessage', label: 'Connection Message', type: 'copyable_text' },
            { id: 'cohortInfo', label: 'Cohort Details', type: 'metadata' },
        ],

        display: {
            showRecommendation: false,
        },
    },

    deal_sourcing: {
        id: 'deal_sourcing',
        label: 'Deal Sourcing',
        description: 'Scans Tracxn LiveDeals emails, runs Research Analyst synthesis, enriches via Cowork People Search, and routes to CEO Work Inbox for approval.',
        icon: 'FunnelSimple',
        path: '/agents/deal-sourcing',
        designVersion: 'v0',
        group: 'sourcing',

        actions: [
            // Send Email is rendered as an <a href="mailto:..."> anchor (not a click
            // handler) but the label + intent must still come from this registry
            // so DealCard._ActionBar reads them through the same path as every
            // other action — eliminates the hand-rolled hardcoded intent.
            // Intent: neutral, because mailto does not commit data to our backend
            // (it hands off to the OS mail client). Authority: WI-LIVEDEALS-DEAL-
            // CARD-ROOT-CAUSE-FIX-001 §C1 + DESIGN_LANGUAGE_SKILL.md §4.
            {
                id: 'send_email',
                label: 'Send Email',
                intent: 'neutral',
                dropdown: null,
            },
            {
                id: 'pass',
                label: 'Pass',
                intent: 'negative',
                loadingLabel: 'Passing...',
                dropdown: {
                    type: 'pass_reasons',
                    items: [
                        { id: 'wrong_stage', label: 'Wrong stage' },
                        { id: 'wrong_sector', label: 'Wrong sector' },
                        { id: 'raise_too_small', label: 'Raise too small' },
                        { id: 'team_concern', label: 'Team concern' },
                        { id: 'not_now', label: 'Not the right time' },
                        { id: 'already_pipeline', label: 'Already in pipeline' },
                    ],
                },
            },
            {
                id: 'view_deck',
                label: 'View Deck',
                intent: 'neutral',
                dropdown: null,
            },
            {
                id: 'contacted',
                label: 'Mark as Contacted',
                intent: 'primary',
                loadingLabel: 'Updating...',
            },
        ],

        tabs: [
            { id: 'pending',  label: 'Pending',  defaultOpen: true },
            { id: 'pipeline', label: 'Contacted', defaultOpen: false },
            { id: 'archive',  label: 'Archive',  defaultOpen: false },
        ],

        labels: {
            pendingEmpty: 'No deals waiting for review — run the Tracxn LiveDeals pipeline to scan for new deals.',
            pipelineEmpty: 'No contacted deals yet.',
            archiveEmpty: 'Passed and filtered deals will appear here.',
            contactedSuccess: 'Marked as contacted.',
            contactedError: 'Could not update — please try again.',
            contactedLabel: 'Mark as Contacted',
            contactingLabel: 'Updating...',
            pendingEnrichmentLabel: 'Pending Enrichment',
            enrichingLabel: 'Enriching',
            contactedDoneLabel: 'Contacted',
            passedLabel: 'Passed',
            filteredOutLabel: 'Filtered Out',
            passSuccess: 'Deal passed.',
            passError: 'Could not pass — please try again.',
            reachOutSuccess: 'Outreach sent.',
            reachOutError: 'Could not send outreach — please try again.',
            sendEmailLabel: 'Send Email',
            connectLinkedInLabel: 'Connect on LinkedIn',
            noContactFound: 'No contact found — check Tracxn manually.',
            noContactFoundLabel: 'No Contact Found',
            noDeckAvailableLabel: 'No Deck Available',
            viewDeckCopiedLabel: 'Copied',
            arrSignalLabel: 'ARR Signal',
            passChooseReasonAriaLabel: 'Pass on deal — choose reason',
            copyDeckAriaLabel: 'Copy deck path',
            openFounderLinkedinAriaPrefix: 'Open',
            linkedinDetailsAriaSuffix: 'LinkedIn details',
            // Empty-state tooltips for muted/disabled action bar buttons.
            // Each tooltip explains why the button is disabled and what the
            // CEO can do next, so the disabled state reads as a state, not
            // a bug. Authority: WI-LIVEDEALS-DEAL-CARD-ROOT-CAUSE-FIX-001 §F2.
            noContactTooltip: 'Founder email not yet populated. Check Tracxn manually or wait for Cowork enrichment.',
            noDeckTooltip: 'Deck not available yet. Cowork uploads it during enrichment.',
            inflightTooltip: 'Cowork enrichment is in progress. The Contacted button activates once the deal reaches review.',
            // RA status surfaces — five-branch render in DealCard._RAStatusSection.
            researchPendingLabel: 'Research pending.',
            researchEmptyLabel: 'Research ran — no public coverage found.',
            researchFailureFallback: 'Research did not complete — reason: {{reason}}.',
            // Traction + investor empty-state copy (replaces silent null-guards).
            tractionEmpty: 'No traction signal found in public research.',
            investorsEmpty: 'No public investor data found.',
            // Founder detail panel
            founderPanelLinkedinSection: 'LinkedIn Profile',
            founderPanelMessageSection: 'LinkedIn Message',
            founderPanelMessageForFounderPrefix: 'Message for',
            founderPanelNoMessage: 'No outreach message generated yet',
            founderPanelCopy: 'Copy',
            founderPanelCopied: 'Copied',
            founderPanelClose: 'Close founder detail panel',

            // --- Leg 5 (WI-TRACXN-ENRICHMENT-BUILD-LEGS-3-7-001): canonical
            // sector_l3 supersedes the legacy sector_hint chip. Each honest-blank
            // source gets its own copy so a budget-cap pause never reads as
            // "we looked and found nothing" (dispatch's core requirement).
            sectorNoSignalLabel: 'Sector — no confident match',
            sectorLowConfidenceLabel: 'Sector — signal too weak to confirm',
            sectorNotCheckedLabel: 'Sector — not checked yet',
            sectorTagRejectedLabel: 'Sector — tag rejected, needs review',

            // Enrichment resolution state — ruled render (seat-test): RESOLVED
            // needs no extra chip (the sector chip already shows real content).
            // CONFIRMED_ABSENT and UNRESOLVED_AMBIGUOUS each get an explicit,
            // distinct chip so "Tracxn has nothing" is never confused with
            // "unsure — needs a human look" (the fi.we false-negative class).
            enrichmentConfirmedAbsentLabel: 'Tracxn has nothing on this one',
            enrichmentAmbiguousLabel: 'Needs a human look',

            // "Where we stand" — rendered only when sector_l3 is known (an
            // honest-blank sector means we cannot know our stance either).
            // No live accrezeo_views read is wired this leg (AS-3 one-road
            // funnel); the line is deliberately a "not checked" admission,
            // never a fabricated stance and never "new territory".
            whereWeStandHeading: 'Where We Stand',
            stanceNotCheckedTemplate: 'Where we stand in {{sector}} — not checked from this card yet.',

            // Screen-reader announcer templates (aria-live) — Pass / Contacted /
            // View Deck outcomes, success AND failure paths. Authority: the
            // #950/#954 pattern in LinkedInPostAgent.jsx, extended to DealCard.
            passAnnounceSuccess: '{{company}} passed.',
            passAnnounceError: 'Could not pass {{company}} — please try again.',
            contactedAnnounceSuccess: '{{company}} marked as contacted.',
            contactedAnnounceError: 'Could not mark {{company}} as contacted — please try again.',
            viewDeckCopiedAnnounce: 'Deck path copied to clipboard.',
            viewDeckCopyErrorAnnounce: 'Could not copy the deck path — please try again.',
        },

        cardCollapsible: {
            defaultCollapsed: true,
            headerMinHeight: 44,
            caretIcon: 'CaretDown',
            expandedShadow: true,
        },

        detailSections: [
            { id: 'dealSummary',           label: 'Deal Summary',       type: 'metadata' },
            { id: 'founderContact',        label: 'Founder Contact',    type: 'founder_card' },
            { id: 'researcherSynthesis',   label: 'Research Notes',     type: 'rich_text' },
            { id: 'tractionSignal',        label: 'Traction',           type: 'badge' },
            { id: 'deckLink',              label: 'Pitch Deck',         type: 'file_link' },
            { id: 'outreachTemplates',     label: 'Outreach Templates', type: 'outreach_panel' },
        ],

        display: {
            showRecommendation: false,
        },
    },

    deal_evaluator: {
        id: 'deal_evaluator',
        label: 'Deal Evaluator',
        description: 'Analyzes pitch decks through 10 investment parameters with Associate/Red Team/Director pipeline.',
        icon: 'FileText',
        path: '/agents/deal-evaluator',
        designVersion: 'v1',
        group: 'analysis',

        // Carry-chain hop labels (DS-D1) — UI copy lives in config, never hardcoded in the card.
        carryChainLabels: {
            problem_to_solution: 'Problem → Solution',
            problem_solution_to_market: 'Problem + Solution → Market',
        },

        // Cross-sector context appendix (WI-DEALSCREEN-READER-READAI-AND-KNOWLEDGE-TRUTH-001
        // Leg 1). The firm's own accepted cross-sector patterns for this sector, shown as
        // background reading beside the screen. Observe-only: it is read after the verdict
        // is settled and never moves the score, so the note says so in plain words.
        // Sector state fallback (sector tagging row R1, 3 Oct 2026). The wording lives once, in
        // docs/system/SECTOR_TAGGING_LAW.md §8.3, built by the backend (`sector_door.resolve_sector_state`).
        // This is the one sentence the screen must still be able to say when the backend sent NO state (an
        // older response): the screen has not read the record. It must equal the backend's NOT CHECKED
        // label; backend/tests/test_sector_door.py pins that equality.
        sectorStateNotCheckedLabel: 'NOT CHECKED — this screen has not read the startup record. This never means no record exists.',
        // The compact form (Law §8.4) for a card or a pane that got no `sector_state` from the backend. It must equal the
        // backend's NOT CHECKED `compact_label`; backend/tests/test_deal_card_sector_state.py pins that equality. A compact
        // surface prints `sector_state.compact_label` and otherwise this line: never the screen's own sector text.
        sectorStateNotCheckedCompactLabel: 'NOT CHECKED — not read on this surface',

        crossSectorContextLabels: {
            heading: 'Patterns We Have Accepted',
            observeOnlyNote: 'Background only — these did not affect this verdict.',
            countSuffix: 'accepted pattern',
            countSuffixPlural: 'accepted patterns',
        },

        actions: [
            {
                id: 'run_fca',
                label: 'Run First Cut Analysis',
                intent: 'ai',
                position: 'left',
            },
            {
                id: 'request_revision',
                label: 'Apply Adjustments',
                intent: 'neutral',
                position: 'middle',
            },

            {
                id: 'export_pdf',
                label: 'Export PDF',
                intent: 'neutral',
                position: 'header',
            },
        ],

        detailSections: [
            { id: 'directionalSignal', label: 'Directional Signal', type: 'insight_block' },
            { id: 'parameters', label: 'Parameter Analysis', type: 'parameter_bar_list', count: 10 },
            { id: 'recommendation', label: 'Recommendation', type: 'insight_block' },
            { id: 'gapList', label: 'Information Gaps', type: 'text_list' },
            { id: 'nextDataAsk', label: 'Next Data Ask', type: 'text_list' },
            { id: 'thesisSignals', label: 'Thesis Signals', type: 'tag_list' },
        ],

        inboxCard: {
            statBoxes: [
                { id: 'recommendation', source: 'deal_screen.call_decision', type: 'category_tag' },
                { id: 'stage', source: 'deal_screen.detected_stage', type: 'text' },
                { id: 'sector', source: 'payload.sector_state.compact_label', type: 'text' },
            ],
            signalField: 'deal_screen.call_decision_reason',
            detailRedirect: '/agents/deal-evaluator',
        },

        pipeline_phases: [
            { key: 'reading', label: 'Reading The Deck', order: 1 },
            { key: 'researching', label: 'Researching The Market', order: 2 },
            { key: 'screening', label: 'Writing The Deal Screen', order: 3 },
        ],

        inputConfig: {
            deckUpload: { enabled: true, label: 'Upload Pitch Deck', accept: '.pdf', placeholder: 'Upload PDF deck...' },
            startupSelect: { enabled: false, label: 'Select Startup', placeholder: 'Choose startup...' }, // WI-DS-DETECT-002: replaced by auto-detection flow
        },

        stageLabels: {
            pre_seed: 'Pre-Seed',
            seed: 'Seed',
            pre_series_a: 'Pre-Series A',
            series_a: 'Series A',
            series_b_plus: 'Series B+',
        },

        callDecisionLabels: {
            lean_in: 'Lean In',
            hold: 'Hold',
            pass: 'Pass',
            worth_a_call: 'Worth a Call',
            not_worth_a_call: 'Not Worth a Call',
        },

        sectionLabels: {
            problem: 'Problem',
            solution: 'Solution',
            market_signal: 'Market Signal',
        },

        // WI-3: Market two-layer sub-header labels (Reality context on top, Pull below).
        marketLayerLabels: {
            context: 'MARKET CONTEXT',
            pull: 'MARKET PULL',
        },

        adjustAnalysisButton: {
            id: 'adjust_analysis',
            label: 'Adjust Analysis',
            intent: 'neutral',
            position: 'above_action_bar',
        },

        adjustAnalysisSidePanel: {
            title: 'Adjust Analysis',
            reanalyseButton: {
                label: 'Apply Adjustments',
                loadingLabel: 'Applying\u2026',
            },
            closeLabel: 'Close',
            sections: [
                { id: 'problem',       label: 'Problem' },
                { id: 'solution',      label: 'Solution' },
                { id: 'market_signal', label: 'Market Signal' },
            ],
        },

        // WI-DEAL-SCREEN-CALIBRATION-UI-001: Section-card calibration footer
        // Authority: docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md \u00a714 TC-DS-CAL-01..03 + \u00a715 UX states.
        // Bound by SectionCalibrationFooter.jsx (planned). Token bindings:
        // SECTION_PANEL.adjustFooter (container), ACTION_BUTTONS.rerun (buttons),
        // VERDICT_PILL.moderate (overridden stamp), DS_COLORS.text.brand (accepted stamp),
        // DS_TYPOGRAPHY.actionBtn (button labels), DS_TYPOGRAPHY.microLabel (refined badge).
        calibrationFooter: {
            acceptButton:      { id: 'cal_accept',   label: 'Accept verdict', intent: 'neutral' },
            overrideButton:    { id: 'cal_override', label: 'Override',        intent: 'neutral' },
            refineProseButton: { id: 'cal_refine',   label: 'Refine prose',    intent: 'neutral', icon: 'PencilSimple' },
            editButton:        { id: 'cal_edit',     label: 'Edit',            intent: 'neutral', ariaLabel: 'Edit override' },
            acceptedStamp:     { label: 'You accepted', tone: 'navy' },
            overriddenStampTemplate: 'You set: {ceo_verdict} (was: {agent_verdict})',
            refinedBadge:      { label: 'CEO refined', tone: 'navy' },
            undoLink:          { label: 'Undo' },
            toasts: {
                acceptSuccess:   'Verdict accepted',
                overrideSuccess: 'Verdict overridden',
                refineSuccess:   'Your version saved',
                error:           'Could not save \u2014 try again',
            },
        },

        // WI-DEAL-SCREEN-CALIBRATION-UI-001: Verdict override popover (TC-DS-CAL-02)
        // Bound by VerdictOverridePopover.jsx (planned). Verdict chip styling driven by
        // VERDICT_PILL.{strong, moderate, weak, unclear} from ui-invariants.js (frozen 18 March 2026).
        // verdictOptions.value mirrors backend constants.py DealScreenVerdicts.
        verdictOverridePopover: {
            title:               'Pick the verdict you would record',
            reasonLabel:         'Why? (optional)',
            reasonPlaceholder:   'Pricing claim contradicted by current ISRO comparable\u2026',
            reasonMaxChars:      500,
            cancelLabel:         'Cancel',
            confirmLabel:        'Save',
            loadingLabel:        'Saving\u2026',
            verdictOptions: [
                { value: 'strong',   label: 'Strong'   },
                { value: 'moderate', label: 'Moderate' },
                { value: 'weak',     label: 'Weak'     },
                { value: 'unclear',  label: 'Unclear'  },
            ],
        },

        // WI-DEAL-SCREEN-CALIBRATION-UI-001: Inline prose editor (TC-DS-CAL-03)
        // Bound by SectionProseEditor.jsx (planned). Mirrors the LinkedInPostDetail
        // edit-mode pattern at frontend/src/components/inbox/detailRenderers/LinkedInPostDetail.jsx:229-447,
        // but uses the shadcn <Textarea> primitive (frontend/src/components/ui/textarea.jsx),
        // not a raw HTML textarea, per the .impeccable.md "shared components before custom" rule.
        proseEditor: {
            enterLabel:           'Refine prose',
            cancelLabel:          'Cancel',
            saveLabel:            'Save my version',
            loadingLabel:         'Saving\u2026',
            unsavedMarker:        'Unsaved changes',
            viewAgentVersionLink: 'View agent version',
            viewMyVersionLink:    'View my version',
            textareaPlaceholder:  'Refine the analysis here. The agent will read this on the next run.',
            textareaMinHeight:    '200px',
            textareaMaxChars:     8000,
        },

        // WI-DEAL-SCREEN-CALIBRATION-UI-001: Call-outcome strip (TC-DS-CAL-04)
        // Bound by CallOutcomeStrip.jsx (planned). Mirrors the pass-with-note pattern
        // in DealInspectorPane.jsx:296-543. Distinct from the deal-level Active/Passed
        // decision recorded via /decision endpoint (top-level decisions array) \u2014 this
        // strip records the call-level outcome (did the CEO actually take the meeting?).
        callOutcomeStrip: {
            tookCallButton:        { id: 'cal_took',    label: 'I took the call',    intent: 'neutral' },
            skippedCallButton:     { id: 'cal_skipped', label: 'I skipped the call', intent: 'neutral' },
            noteLabel:             'Anything worth noting? (optional)',
            notePlaceholder:       'Pricing too thin to take to founder call\u2026',
            noteMaxChars:          500,
            cancelLabel:           'Cancel',
            confirmLabel:          'Save',
            loadingLabel:          'Saving\u2026',
            tookCallStampTemplate: 'You took the call \u00b7 {date}',
            skippedCallStampTemplate: 'You skipped \u00b7 {date}',
            noteHintTemplate:      'note: {note}',
            undoLabel:             'Undo',
            toasts: {
                success: 'Call outcome recorded',
                error:   'Could not save \u2014 try again',
            },
        },

        // WI-PROGRESS-CARD-POC: Pipeline progress card config
        progressCard: {
            title: 'Analysis in Progress',
        },

        // WI-PROGRESS-CARD-POC: Terminal state labels (mirrors backend deal_screen_pipeline_status_config.json)
        terminalStateLabels: {
            complete: 'Deal Screen Complete',
            failed:   'Deal Screen Encountered an Issue',
            partial:  'Partial Deal Screen Available',
        },

        // Rewrite state labels — sourced by DealScreenDetailView.jsx
        rewritingSectionLabel: 'Rewriting\u2026',
        rewriteCompleteLabel: 'Done',
        rewriteErrorLabel: 'Could not rewrite \u2014 try again',

        // FCA action button labels — sourced by DealEvaluatorAgent.jsx
        runFcaLabel: 'Run First Cut Analysis',
        rerunWithTranscriptLabel: 'Re-run with Transcript',
        runPostCallLabel: 'Run Post-Call Analysis',
        exportPdfLabel: 'Export PDF',
        reanalysingLabel: 'Reanalysing\u2026',

        rerunLabel: 'Re-run from Deck',
        rerunLoadingLabel: 'Re-running from deck\u2026',
        rerunSuccessToast: 'Re-analysis started \u2014 results will update shortly.',
        rerunErrorToast: 'Could not start re-analysis. Please try again.',
        rerunOverlayPhasePrefix: '',

        // DS-CARD-V4: Call decision style — maps to CALL_BADGE + DEAL_SCREEN_CARD + DS_DETAIL_VIEW in ui-invariants.js
        // WI-DS-HARDCODE-001: All hex values replaced with token references from ui-invariants.js
        callDecisionStyle: {
            worth_a_call: {
                badgeKey:        'worthACall',
                accentBarColor:  DEAL_SCREEN_CARD.accentBar.worthACall,
                rationaleKey:    'worthACall',
                label:           'Worth a Call',
            },
            lean_in: {
                badgeKey:        'worthACall',
                accentBarColor:  DEAL_SCREEN_CARD.accentBar.worthACall,
                rationaleKey:    'worthACall',
                label:           'Worth a Call',
            },
            not_worth_a_call: {
                badgeKey:        'notWorthACall',
                accentBarColor:  DEAL_SCREEN_CARD.accentBar.notWorthACall,
                rationaleKey:    'notWorthACall',
                label:           'Not Worth a Call',
            },
            hold: {
                badgeKey:        'notWorthACall',
                accentBarColor:  DEAL_SCREEN_CARD.accentBar.notWorthACall,
                rationaleKey:    'notWorthACall',
                label:           'Hold',
            },
            pass: {
                badgeKey:        'notWorthACall',
                accentBarColor:  DEAL_SCREEN_CARD.accentBar.notWorthACall,
                rationaleKey:    'notWorthACall',
                label:           'Pass',
            },
        },

        // WI-DEAL-SCREEN-VERDICT-DISPLAY-FIX-001: CEO decision display authority.
        // decisions[].value (the CEO's recorded call) is a DIFFERENT concept from
        // call_decision (the agent's recommendation). When the CEO has recorded a
        // decision, the header badge reflects THIS, not the agent verdict. badgeKey
        // reuses the existing CALL_BADGE chrome so the visual matches the agent badge.
        ceoDecisionDisplay: {
            active: { badgeKey: 'worthACall',    label: 'Active' },
            passed: { badgeKey: 'notWorthACall', label: 'Passed' },
            agentNotePrefix: 'Agent view',
        },

        // DS-CARD-V5: Per-verdict visual config
        // WI-DS-HARDCODE-001: All hex values replaced with token references
        verdictConfig: {
            strong:   { kpiValueColor: KPI_TILE.accentBar.byVerdict.strong, accentBarColor: KPI_TILE.accentBar.byVerdict.strong, sectionBorderColor: SECTION_PANEL.leftBorderByVerdict.strong },
            moderate: { kpiValueColor: KPI_TILE.accentBar.byVerdict.moderate, accentBarColor: KPI_TILE.accentBar.byVerdict.moderate, sectionBorderColor: SECTION_PANEL.leftBorderByVerdict.moderate },
            weak:     { kpiValueColor: KPI_TILE.accentBar.byVerdict.weak, accentBarColor: KPI_TILE.accentBar.byVerdict.weak, sectionBorderColor: SECTION_PANEL.leftBorderByVerdict.weak },
            unclear:  { kpiValueColor: DS_COLORS.text.secondary, accentBarColor: DS_COLORS.text.secondary, sectionBorderColor: SECTION_PANEL.leftBorderByVerdict.unclear },
        },

        // DS-FLEX-INPUT: Evidence basis display strings by input_type
        // Shown in the detail view header to explain what evidence the analysis is based on.
        dsEvidenceBasis: {
            deck_full:      'Full Deck Analysis',
            deck_one_pager: 'One-Pager Analysis',
            url:            'URL-Based Analysis',
            text:           'Text-Based Analysis',
            name_only:      'Name-Only Analysis — Research Only',
        },

        // DS-FLEX-INPUT: Confidence qualifier suffixes on verdict pills
        // Appended to verdict label when input evidence is thin.
        confidenceQualifiers: {
            deck_full:      '',
            deck_one_pager: ' (limited deck)',
            url:            ' (from URL)',
            text:           ' (from text)',
            name_only:      ' (research only)',
        },

        // DS-FLEX-INPUT: Input type display labels for the UI
        inputTypeLabels: {
            deck_full:      'Pitch Deck',
            deck_one_pager: 'One-Pager',
            url:            'URL',
            text:           'Pasted Text',
            name_only:      'Name Only',
        },

        // DS-FLEX-INPUT: Input mode selector chip labels
        inputModeChips: [
            { key: 'deck', label: 'Upload Deck' },
            { key: 'url', label: 'Paste URL' },
            { key: 'text', label: 'Paste Text' },
            { key: 'name', label: 'Name Only' },
        ],

        // DS-FLEX-INPUT: Placeholder strings for flex input fields
        flexInputPlaceholders: {
            startupNameOptional: 'Startup name (optional)',
            startupName:         'Startup name...',
            url:                 'Paste startup or article URL...',
            text:                'Paste startup description, pitch notes, or any text...',
            nameOnlyHint:        'Enter a startup name \u2014 we will research it from scratch using only external sources.',
            startAnalysis:       'Start Analysis',
            startingAnalysis:    'Starting...',
        },

        // FCA Phase 4: Display config for the FCA tab — no component should contain these values directly
        fcaPage: {
            triggerChips: [
                { triggerKey: 'PRE',  label: 'Pre-Call' },
                { triggerKey: 'POST', label: 'Post-Call' },
                { triggerKey: 'KB',   label: 'Market Intel' },
            ],

            sectionVisibility: {
                PRE:  { directionalSignal: true, parameters: true, nextDataAsk: true,  informationGaps: true, thesisSignals: true, previousRuns: true },
                POST: { directionalSignal: true, parameters: true, nextDataAsk: true,  informationGaps: true, thesisSignals: true, previousRuns: true },
                KB:   { directionalSignal: true, parameters: true, nextDataAsk: false, informationGaps: true, thesisSignals: true, previousRuns: true },
            },

            actionButtons: {
                PRE:  ['rerun_with_transcript', 'export_pdf'],
                POST: ['export_pdf'],
                KB:   ['export_pdf'],
            },

            rerunButton: {
                id: 'rerun_with_transcript',
                label: 'Re-run with Transcript',
                intent: 'ai',
                stubMessage: 'Post-call analysis coming soon',
            },

            kbNoticeBar: {
                visibleFor: ['POST'],
                label: 'Ground truth from founder call — overrides pre-call assumptions where contradicted',
            },

            parameterSectionLabel: 'PARAMETER ANALYSIS',

            noFcaMessage: 'Select a deal screen to view its First Cut Analysis.',
        },

        // Gate C — Decision layer label maps.
        // Authority: task-display-config.js (this file). Component agent reads ONLY from here.
        // All values are plain business language — no raw underscores, no ALL-CAPS rendered to user.

        // Band word labels: human-readable form of the internal STRONG / WEAK / NO_SIGNAL enum.
        // Renderer uses these for tile headings and aria labels. Fallback: title-case of the enum.
        bandWordLabels: {
            NO_SIGNAL: 'No Signal',
            WEAK:      'Weak Signal',
            STRONG:    'Strong Signal',
        },

        // Evidence grade labels: human-readable form of the backend evidence grade enum.
        // Includes both upper-case (persisted enum) and lower-case (live data) variants so the
        // renderer can do a single lookup without normalisation. Fallback: title-case of raw value.
        evidenceGradeLabels: {
            STRONG:       'Strong evidence',
            MODERATE:     'Moderate evidence',
            WEAK:         'Weak evidence',
            revealed:     'Found externally',
            verified:     'Independently verified',
            stated:       'From the deck (unverified)',
            deck_claimed: 'From the deck (unverified)',
        },

        // Provenance labels: human-readable form of backend provenance / correction tokens.
        // NOTE: use_exact is intentionally omitted — renderer must not render it.
        // deck_claimed and stated share the same display copy but remain distinct keys so
        // the renderer can differentiate them in future without a config change.
        provenanceLabels: {
            revealed:      'Found externally',
            verified:      'Independently verified',
            deck_claimed:  'From the deck (unverified)',
            stated:        'From the deck (unverified)',
            down_corrected: 'Corrected downward',
            blocked:       'Could not verify',
            stripped:      'Figure removed',
        },

        // Source tag labels: human-readable form of known source_tag raw values.
        // Title-case fallback in renderer for any value not listed here.
        // No underscores, no ALL-CAPS rendered to user.
        sourceTagLabels: {
            revealed:    'External research',
            verified:    'Independently verified',
            stated:      'Deck',
            deck_claimed: 'Deck',
            perplexity:  'Web research',
            web:         'Web research',
            deck:        'Deck',
            founder:     'Founder',
            computed:    'Calculated',
        },

        // Decision layer: copy and structural config for the 4-row redesign.
        // Consumed by DealScreenDetailView.jsx decision layer renderer.
        decisionLayer: {
            recommendationLabel:    'Recommendation',
            nameNotDetected:        'Company name not detected',
            contrarianLabel:        'The counter-case',
            contrarianFloorNote:    'This is the weakest leg of the call.',
            oneQuestionLabel:       'Question for the call',
            oneQuestionSourceLabel: 'From',
            carryChainFailBanner:   'The reasoning chain has a broken link — read the section detail before deciding.',
            overallTileLabel:       'Overall',
            tileExpandLabel:        'Reasoning',
            // Used by renderer's net-positive-on-weak guard + amber-bar logic.
            // Any tile whose band falls in this list triggers the amber accent treatment.
            weakBands:              ['WEAK', 'NO_SIGNAL'],
        },

        // WI-DS-STYLE-GATE-UI-SURFACE-001: Style gate block notice config.
        // All copy for the StyleGateNotice banner lives here — zero inline JSX text.
        // enforce_eligible classes: DS-STYLE-BANNED-PHRASE, DS-STYLE-MACHINE-TOKEN,
        //   DS-STYLE-NUMERIC-SCORE, DS-STYLE-MULTI-QUESTION, DS-STYLE-READING-ORDER,
        //   DS-STYLE-FILENAME-NAME, DS-STYLE-NAME-IN-OPENING.
        // Authority: scripts/invariants/verify_deal_screen_style.py ENFORCE_ELIGIBLE_CLASSES.
        styleGate: {
            blockedStatus: 'blocked',
            noticeHeading: 'This analysis was held back',
            // WI-DS-PROGRESS-VISIBILITY-BUILD-001 (Change C): Honest copy.
            // Old: "get a clean version" — implies a guaranteed outcome.
            // Real: temperature-0 LLM is not bit-deterministic; Re-run usually
            // avoids the trigger phrase but may recur. Also: Re-run re-reads the
            // SAME cached deck, takes ~3 minutes, and costs a full run.
            noticeSubtext: 'We held this analysis back because of a readability issue. Re-running reads the same deck again and usually produces a clean version — though it is not guaranteed. This takes around 3 minutes.',
            identifierPrefix: 'Screen',
            rerunLabel: 'Re-run analysis',
            rerunningLabel: 'Re-running…',
            defaultClassLabel: 'A readability issue we flag automatically',
            classLabels: {
                'DS-STYLE-BANNED-PHRASE':    'Contains language that our writing standards do not allow',
                'DS-STYLE-MULTI-QUESTION':   'Multiple questions welded into one slot',
                'DS-STYLE-NAME-IN-OPENING':  'Opening line names the wrong company',
                'DS-STYLE-FILENAME-NAME':    'Used the filename as the company name',
                'DS-STYLE-MACHINE-TOKEN':    'A bracketed internal token leaked into the writing',
                'DS-STYLE-NUMERIC-SCORE':    'A raw internal score leaked into the writing',
                'DS-STYLE-READING-ORDER':    'The sections came in the wrong order',
            },
        },
    },
};

// ============================================================
// FCA PAGE LABELS
// Labels sourced from backend/config/fca_pipeline_status_config.json
// Trigger labels, recommendation labels, confidence labels for the FCA tab panel.
// ============================================================

export const FCA_PAGE_CONFIG = {
    triggerLabels: {
        pre:  { displayLabel: 'Pre-Call Analysis',       shortLabel: 'Pre-Call' },
        post: { displayLabel: 'Post-Call Analysis',      shortLabel: 'Post-Call' },
        kb:   { displayLabel: 'Market Intelligence Run', shortLabel: 'Market Intel' },
    },
    recommendationLabels: {
        lean_in: { displayLabel: 'Lean In',        colourIntent: 'positive' },
        hold:    { displayLabel: 'Hold',            colourIntent: 'cautious' },
        pass:    { displayLabel: 'Pass',            colourIntent: 'negative' },
        kb_only: { displayLabel: 'Sector Learning', colourIntent: 'neutral' },
    },
    // NON-RENDERING — backend tracking only. Verdict display uses VERDICT_PILL from ui-invariants.js.
    // Never reference these labels in any JSX render path.
    confidenceLabels: {
        low:    { displayLabel: 'Early Signal',        colourIntent: 'cautious' },
        medium: { displayLabel: 'Building Conviction', colourIntent: 'neutral' },
        high:   { displayLabel: 'Strong Evidence',     colourIntent: 'positive' },
    },
    sectionLabels: {
        gapList:           'Information Gaps',
        nextDataAsk:       'Next Data Ask',
        thesisSignals:     'Thesis Signals',
        previousRuns:      'Previous Runs',
        parameterAnalysis: 'Parameter Analysis',
        directionalSignal: 'Directional Signal',
    },
    progressStates: {
        pre: [
            { key: 'reading',        label: 'Reading the deck' },
            { key: 'researching',    label: 'Researching the market' },
            { key: 'evaluating',     label: 'Evaluating each parameter' },
            { key: 'stress_testing', label: 'Stress-testing the analysis' },
            { key: 'synthesising',   label: 'Writing the final view' },
        ],
        post: [
            { key: 'reading',      label: 'Reading the transcript' },
            { key: 'evaluating',   label: 'Comparing with pre-call view' },
            { key: 'synthesising', label: 'Writing the updated analysis' },
        ],
        kb: [
            { key: 'reading',      label: 'Reading the deck' },
            { key: 'researching',  label: 'Researching the sector' },
            { key: 'evaluating',   label: 'Writing the Analysis' },
            { key: 'synthesising', label: 'Extracting market signals' },
        ],
    },
    completeLabel: 'Analysis Complete',
    errorStates: {
        deck_not_found:     'The deck file is no longer available \u2014 please re-upload the deck to run this analysis.',
        pipeline_failed:    'Analysis could not be completed \u2014 please try again.',
        post_no_transcript: 'Please add a transcript file first.',
    },
    rerunButton: {
        label: 'Re-run Analysis',
        loadingLabel: 'Re-running\u2026',
        successToast: 'Re-analysis started \u2014 results will update shortly.',
        errorToast: 'Could not start re-analysis. Please try again.',
    },
    fcaButtons: {
        rerun: {
            id: 'fca_rerun',
            label: 'Re-run Analysis',
            loadingLabel: 'Re-running\u2026',
            intent: 'ai',
            position: 'top_right',
        },
        exportPdf: {
            id: 'fca_export_pdf',
            label: 'Export PDF',
            intent: 'neutral',
            position: 'top_right',
        },
    },
    fcaAdjustPanel: {
        triggerButton: {
            label: 'Adjust Analysis',
            intent: 'neutral',
        },
        title: 'Adjust Analysis',
        reanalyseButton: {
            label: 'Apply Adjustments',
            loadingLabel: 'Applying\u2026',
        },
        closeLabel: 'Close',
    },
    parameterVerdictOptions: [
        { value: 'strong_signal',       label: 'Strong Signal' },
        { value: 'promising_but_early', label: 'Promising but Early' },
        { value: 'neutral',             label: 'Neutral' },
        { value: 'concerning',          label: 'Concerning' },
        { value: 'structural_weakness', label: 'Structural Weakness' },
    ],
    fcaKnobDefs: [
        { key: 'verify_externally',       label: 'Verify Externally' },
        { key: 'show_reasoning',          label: 'Show Reasoning' },
        { key: 'ground_in_india',         label: 'Ground in India' },
        { key: 'expand_competitive_view', label: 'Expand Competitive View' },
        { key: 'increase_skepticism',     label: 'Increase Skepticism' },
        { key: 'flag_inaccuracy',         label: 'Flag Inaccuracy' },
        { key: 'what_breaks_this',        label: 'What Breaks This' },
    ],
    fcaParameterTable: {
        columns: {
            parameter: 'Parameter',
            verdict: 'Verdict',
            finding: 'Finding',
        },
        expandLabel: 'See full analysis',
        collapseLabel: 'Collapse',
    },
};

// ============================================================
// LINKEDIN SIGNAL PILL MAPPING
// Maps signal_strength values from LinkedIn post agent to VERDICT_PILL verdicts.
// Used by LinkedInPostDetail to render a pill instead of dots.
// ============================================================

export const linkedInSignalPill = {
    strong_signal: { label: 'Strong Signal', verdict: 'strong' },
    signal:        { label: 'Signal',        verdict: 'moderate' },
    noise:         { label: 'Noise',         verdict: 'weak' },
};

// ============================================================
// INBOX FILTER CONFIG
// Governs which agent filters appear as pinned chips vs. overflow dropdown.
// Config-driven: adding a future agent requires adding an entry here — zero component changes.
// ============================================================

export const INBOX_FILTER_CONFIG = {
    pinned: [
        { id: 'all', label: 'All' },
        { id: 'article_review', label: 'Sector Intelligence' },
        { id: 'campaign', label: 'Founder Outreach' },
    ],
    overflow: [
        { id: 'linkedin_post', label: 'Post Studio' },
        { id: 'lead_gen_scout', label: 'LinkedIn Scout' },
        { id: 'tracxn_livedeals', label: 'Deal Sourcing' },
        { id: 'first_look', label: 'Deal Sourcing' },
    ],
    overflowLabel: 'More',
};

// ============================================================
// RECOMMENDATION GROUPS
// Maps recommendation types from agents to display groups and intents.
// Used by inbox to group and color-code items.
// ============================================================

export const RECOMMENDATION_GROUPS = [
    { type: 'article_analysis',  group: 'Sector Intelligence', intent: 'info',    agentId: 'sector_intelligence' },
    { type: 'email_outreach',    group: 'Founder Outreach',    intent: 'primary', agentId: 'founder_outreach' },
    { type: 'linkedin_post',     group: 'Post Studio',         intent: 'info',    agentId: 'post_studio' },
    { type: 'strategy_insight',  group: 'Strategy Insights',   intent: 'neutral', agentId: null },
    { type: 'system_alert',      group: 'System',              intent: 'warning', agentId: null },
    { type: 'linkedin_scout',    group: 'LinkedIn Scout',      intent: 'info',    agentId: 'lead_gen_scout' },
    { type: 'deal_screen',       group: 'Deal Evaluator',  intent: 'info',    agentId: 'deal_evaluator' },
    { type: 'tracxn_livedeals',    group: 'Deal Sourcing',   intent: 'info',    agentId: 'deal_sourcing' },
    { type: 'first_look',          group: 'Deal Sourcing',   intent: 'info',    agentId: 'deal_sourcing' },
    { type: 'principle_promotion', group: 'Principle Library', intent: 'info',  agentId: 'principle_library' },
    { type: 'startup_review',      group: 'Sector Intelligence', intent: 'info', agentId: 'sector_intelligence' },
];

// ============================================================
// V7 RECOMMENDATION GROUPS (Phase 9i)
// Groups inbox items by RECOMMENDATION type (save/review/skip),
// NOT by agent type. This is the V7 frozen prototype grouping model.
// V7 lines 132-136, 565.
// ============================================================

export const V7_RECOMMENDATION_GROUPS = [
    { rec: 'save',   title: 'Ready to approve',     intent: 'positive' },
    { rec: 'review', title: 'Needs your review',     intent: 'warning' },
    { rec: 'skip',   title: 'Recommended to skip',   intent: 'negative' },
];

// ============================================================
// INTENT-TO-BUTTON MAPPING
// Resolves action intents to concrete Tailwind button classes.
// Authority: UI_DESIGN_CONTRACT_V3.md Section C.3
// Zero hex codes — all classes reference Tailwind tokens from tailwind.config.js.
// ============================================================

// DS-DESIGN-MIGRATION: Hover rule — universal navy hover per DESIGN_LANGUAGE_SKILL.md
// Primary: deepen to brand-dark (#12125a). Secondary/neutral/muted: border+text → brand-900.
// Destructive (negative): border → brand-900, text stays red (red is the signal).
// Warning mapped to secondary (amber borders banned per design language).
// DS-DESIGN-MIGRATION: Intent-to-button mapping per DESIGN_LANGUAGE_SKILL.md Section 4
// AI Action: violet fill — only for buttons that trigger an AI pipeline.
// Danger (negative): ghost, red text, red border+fill on hover. Never disabled.
// Warning mapped to neutral (amber borders banned per design language).
export const INTENT_TO_BUTTON = {
    primary:  { bg: 'bg-brand-900', text: 'text-white',      border: 'border-brand-900', hover: 'hover:bg-brand-dark',                              fill: 'filled' },
    ai:       { bg: 'bg-violet-600', text: 'text-white',     border: 'border-violet-600', hover: 'hover:bg-violet-700',                              fill: 'filled' },
    warning:  { bg: 'bg-white',     text: 'text-slate-600',  border: 'border-slate-200', hover: 'hover:border-brand-900 hover:text-brand-900',       fill: 'outline' },
    negative: { bg: 'bg-white',     text: 'text-red-600',    border: 'border-slate-200', hover: 'hover:border-red-600 hover:bg-red-50',              fill: 'outline' },
    neutral:  { bg: 'bg-white',     text: 'text-slate-700',  border: 'border-slate-200', hover: 'hover:border-brand-900 hover:text-brand-900',       fill: 'outline' },
    // Inflight = "pipeline is doing something, not your action yet". Active outline,
    // brand colour, no opacity dim. Used on Mark-as-Contacted when status is
    // pending_enrichment / enriching so the CEO sees "this is in progress" rather
    // than a dimmed grey button that reads like a bug. Authority: WI-LIVEDEALS-
    // DEAL-CARD-ROOT-CAUSE-FIX-001 §F2.
    inflight: { bg: 'bg-white',     text: 'text-brand-700',  border: 'border-brand-200', hover: 'hover:bg-brand-50 hover:border-brand-900',            fill: 'outline' },
    muted:    { bg: 'bg-white',     text: 'text-slate-400',  border: 'border-slate-200', hover: 'hover:border-brand-900 hover:text-brand-900',       fill: 'outline' },
};

// ============================================================
// BELIEF TYPE TO INTENT MAPPING
// Maps article belief tag types to visual intents.
// Used in detail view belief tag rendering.
// ============================================================

export const BELIEF_TYPE_TO_INTENT = {
    existing: 'positive',   // Confirms existing belief
    contrary: 'negative',   // Contradicts existing belief
    novel:    'info',       // New insight, no prior belief
    adjacent: 'warning',    // Tangentially related
};

// ============================================================
// REJECTION CATEGORIES (Predefined for ML training)
// No free-text allowed. These are structured for supervised learning.
// ============================================================

export const REJECTION_CATEGORIES = [
    { id: 'not_relevant', label: 'Not relevant to fund thesis' },
    { id: 'low_quality', label: 'Low quality / poorly written' },
    { id: 'duplicate', label: 'Already covered / duplicate' },
    { id: 'bad_timing', label: 'Timing not right' },
    { id: 'needs_research', label: 'Needs more research first' },
    { id: 'disagree', label: 'Agent assessment disagrees with my judgment' },
    { id: 'other', label: 'Other' },
];

// ============================================================
// WORK TYPE CONSTANTS
// Canonical string keys for backend work_type values.
// Import these instead of hardcoding the string in components.
// ============================================================
export const WORK_TYPES = {
    ARTICLE_REVIEW:  'article_review',
    CAMPAIGN:        'campaign',
    LINKEDIN_POST:   'linkedin_post',
    LEAD_GEN_SCOUT:  'lead_gen_scout',
    DEAL_SCREEN:     'deal_screen',
    EMAIL_OUTREACH:  'email_outreach',
    ARTICLE_ANALYSIS:'article_analysis',
    TRACXN_LIVEDEALS:'tracxn_livedeals',
    FIRST_LOOK:      'first_look',   // R04 (19 Oct 2026): the generic first-look card, any source
    PRINCIPLE_PROMOTION: 'principle_promotion',
    STARTUP_REVIEW:  'startup_review',
    // Generic task-type keys used by older work items that don't carry an
    // agent-specific work_type. The inbox router maps these to KBTaskDetail /
    // StartupTaskDetail / GenericTaskDetail. Kept here (constants-authority)
    // so the router never needs raw string literals.
    KB:              'kb',
    STARTUP:         'startup',
    REVIEW:          'review',
};

// ============================================================
// AGENT ID CONSTANTS
// Canonical string keys for AGENT_CONFIG agent IDs.
// Import these instead of hardcoding the string in components.
// ============================================================
export const AGENT_IDS = {
    SECTOR_INTELLIGENCE: 'sector_intelligence',
    POST_STUDIO:         'post_studio',
    FOUNDER_OUTREACH:    'founder_outreach',
    LEAD_GEN_SCOUT:      'lead_gen_scout',
    DEAL_EVALUATOR:      'deal_evaluator',
    DEAL_SOURCING:       'deal_sourcing',
};

// ============================================================
// SHARED UI LABELS
// Generic labels used across multiple components that don't belong
// to a specific agent config. Import from here, never hardcode.
// ============================================================
export const SHARED_UI_LABELS = {
    // A count that could not be loaded (WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001,
    // CEO ruling 2026-07-30). It says UNKNOWN and never 0. A zero that actually means
    // "nobody answered" is indistinguishable from a real empty tab, and that is how dead
    // counters stayed invisible for months. Lowercase because it is spoken aloud next to
    // the tab name by a screen reader.
    countUnknown:        'unknown',
    countUnknownSpoken:  'count unknown',

    // Redraft, mounted on the saved-draft card (WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001).
    // The endpoint became real on 2026-07-29 but sat on no screen, so there was nothing to
    // press. It reports a failure as HTTP 200 with status "failed", which means the screen
    // must read the STATUS and never treat a 200 as success — reporting success for work not
    // done is the exact failure this control was rebuilt to stop.
    redraftButton:       'Redraft',
    redraftBusy:         'Redrafting…',
    redraftSuccess:      'Redrafted. The draft below is the new one.',
    redraftNoChange:     'The council ran and returned the same text, so nothing changed.',
    // Used only when the backend gives no reason at all. Every other case shows its real one.
    redraftFailedFallback:
        'The redraft did not produce a new draft, and no reason was given. Your saved draft is unchanged.',

    // ── Work Inbox accept outcomes (WI-IDEA-LANE-EXECUTE-CEO-RULINGS-001 item A, CEO 2026-07-30)
    // One sentence per status literal the accept route can return. Declared here, not typed
    // into the hook, because the omission of ONE literal ('acknowledged') showed the CEO a
    // failure over a successful write and cost him a day believing his buttons were dead.
    // Business-facing English only — a raw backend token spoken by a screen reader is not a
    // sentence. Authority for the closed set: components/inbox/cockpit/acceptStatusOutcomes.js.
    acceptOutcomeApproved:     'Item accepted',
    acceptOutcomeAcknowledged: 'Got it — cleared from your inbox.',
    acceptOutcomeIgnored:      'This item was already processed',
    acceptOutcomeBlocked:      'This could not be sent because the mailbox is not connected right now. Nothing was sent, and the batch is unchanged.',
    acceptOutcomeFailed:       'This item could not be found, so nothing was changed.',
    acceptOutcomeUndeclared:   'Something went wrong, please try again',

    // Deal Screen — run-failure state + retry (WI-DS-STUCK-RUN-RCA-RETRY-001)
    // Business-facing, no raw codes. The backend supplies a specific error_message
    // when it has one; this heading + fallback cover the client-synthesised case.
    dealScreenFailedHeading:   'This analysis couldn’t be completed',
    dealScreenFailedFallback:  'Something interrupted the analysis. You can try running it again.',
    dealScreenRetryButton:     'Retry analysis',
    dealScreenRetryingLabel:   'Restarting…',

    // Navigation / panel controls
    closePanel:          'Close panel',
    closeDetailPanel:    'Close detail panel',
    mainNavigation:      'Main navigation',
    primaryNavigation:   'Primary navigation',
    accrezeoHome:        'Accrezeo home',

    // Generic placeholders
    searchByNameOrEmail: 'Search by name or email...',
    filterByStartup:     'Filter by startup...',
    searchStartup:       'Search startup...',
    searchTools:         'Search tools...',
    findStartupByName:   'Find a startup by name...',
    pasteArticleText:    'Paste article text here...',

    // Filter placeholders
    selectSector:        'Select Sector',
    selectStage:         'Select Stage',
    selectStatus:        'Select Status',
    selectGeography:     'Select Geography',

    // Form placeholders
    reasonPlaceholder:   'Please specify the reason...',
    entryTitle:          'Entry Title',
    briefSummary:        'Brief summary of the content...',
    detailedAnalysis:    'Detailed analysis...',
    decisionRationale:   'Why did you make this decision?',

    // Pagination
    goToPreviousPage:    'Go to previous page',
    goToNextPage:        'Go to next page',

    // Action labels
    copyPost:            'Copy post',
    editPostText:        'Edit post text',
    copyComment:         'Copy comment',
    clearSearch:         'Clear search',

    // Deal evaluator tabs
    dealScreenTab:       'Deal Screen',
    firstCutAnalysisTab: 'First Cut Analysis',

    // Article agent steps
    articleStepReading:  'Reading source',
    articleStepDedup:    'Checking for duplicates',
    articleStepAnalyzing:'Analyzing content',
    articleStepPreparing:'Preparing review',

    // Source remove button
    removeSourceTitle:   'Remove this source',
    removeSourceLabel:   'Remove this article source',

    // Paywall recovery (inline text paste on a paywall_blocked row)
    paywallBlockedMessageDefault: 'Could not read this article — paste the article text below to continue.',
    paywallGenericMessage:        'Could not read — article may be paywalled',
    pasteTextFallbackPlaceholder: 'Paste the full article text here…',
    pasteTextFallbackButton:      'Use Pasted Text',
    pasteTextFallbackSubmitting:  'Analyzing…',
    pasteTextFallbackSuccess:     'Article text received — analyzing now',
    pasteTextFallbackError:       'Could not use pasted text — please try again',
    pasteTextFallbackEmpty:       'Paste the article text before continuing',

    // Article agent friendlyError mapper (kept in sync with friendlyError() in ArticleAgent.jsx)
    friendlyErrorDefault:         'Could not analyze article — please try again',
    friendlyErrorBilling:         'Analysis service temporarily unavailable',
    friendlyErrorTimeout:         'Analysis took too long — please try again',
    friendlyErrorTooShort:        'Article text too short for analysis',

    // Needs Attention panel — outcome_class-aware button labels (WI-ARTICLE-OUTCOME-CLASS-SSOT-001)
    // Served from config so Gate 2b frontend_hardcode_scan does not flag string literals in JSX.
    needsAttentionCountOne:       'article could not be processed.',
    needsAttentionCountMany:      'articles could not be processed.',
    needsAttentionRetry:          'Retry',
    needsAttentionRetrying:       'Retrying',
    needsAttentionAcceptAnyway:   'Accept Anyway',
    needsAttentionAccepting:      'Accepting',
    needsAttentionRemove:         'Remove',

    // Waiting for Review — provenance labels (WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13)
    // A queued article either came from the CEO's own submission or arrived on its
    // own via an automated intake channel. Business-facing only — never the raw
    // input_channel code (e.g. "scraper_inc42_indepth").
    articleProvenanceSelf:  'Added by you',
    articleProvenanceAuto:  'Arrived on its own',
    articleProvenanceEmail: 'Found in your email',

    // Batch cap disclosure (WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13):
    // POST /articles/process splits rather than drops when a run is larger than
    // ARTICLE_MAX_BATCH_SIZE — the excess stays pending, named under
    // batch_capped_count. Never "cap", "batch", or "truncated" — a plain
    // statement of what happens next, read aloud alongside the count.
    articleBatchCappedSuffix: 'more will wait for the next run',

    // Deal screen detail
    editStartupNameTitle:'Edit startup name',
    overallSignalLabel:  'Overall Signal',
    directionalSignal:   'Directional Signal',

    // KB labels
    whyThisMatters:      'Why This Matters',

    // Article review
    strongSignal:        'Strong Signal',
    newPattern:          'New pattern',

    // CEO Office
    strategyInsightsLabel: 'Strategy Insights',
    ceoOfficeSectionsLabel: 'CEO Office sections',

    // Knowledge Base page
    knowledgeBaseTitle:  'Knowledge Base',
    knowledgeBaseDesc:   'Analytics-first content intelligence cockpit',

    // Startups page
    startupsPipelineTitle: 'Startups Pipeline',
    startupsPipelineDesc:  'Manage and track your startup opportunities',

    // Tool Bank page
    ideaBankTitle:       'Idea Bank',
    ideaBankDesc:        'AI tools for deal sourcing, analysis, and content creation',
    editToolTitle:       'Edit tool',
    deleteToolTitle:     'Delete tool',
    // The Tool Bank card had no link element at all until 2026-07-31, so a
    // website entered on the edit form was stored and never displayed. The
    // absence wording is deliberate and plain: a blank space would read as
    // "nothing to report" when it means "we hold no address for this tool".
    toolVisitSite:       'Open tool website',
    toolNoLinkRecorded:  'No website recorded for this tool',
    similarToolsPlaceholder: 'Similar tools',

    // Tool edit form
    toolCategoryPlaceholder: 'e.g., Productivity, Research, Analysis',

    // Founders page
    foundersDesc:            'Manage and track founder relationships',
    founderNamePlaceholder:  'e.g. Elon Musk', // noscan — example name placeholder
    linkedInViewLinkAria:    'Open LinkedIn post in new tab', // noscan — accessibility label copy for the post URL link

    // Deal Evaluator — Stage-and-Analyze staging UI (DS_INPUT_TRIGGER_REDESIGN_2026-06-02)
    analyzeDealButton:       'Analyze Deal',
    analyzingLabel:          'Analyzing…',
    addDeckTranscriptButton: 'Add Deck or Transcript',
    pasteTextButton:         'Paste Text',
    addLinkButton:           'Add Link',
    stagingEmptyHelper:      'Add a deck, transcript, text, or link to begin.',
    stagingCapMessage:       'Maximum 10 inputs staged. Remove one before adding another.',
    stagingPasteTextPlaceholder: 'Paste startup description, pitch notes, or transcript text…',
    stagingLinkPlaceholder:  'Paste a URL…',
    stagingNamePlaceholder:  'Display name (optional)',
    stagingRoleBadgeDeck:    'Deck',
    stagingRoleBadgeTranscript: 'Call transcript',
    stagingRoleBadgeText:    'Text',
    stagingRoleBadgeLink:    'Link',
    removeInputAriaLabel:    'Remove this input',
    detectionBannerInfoPrefix: 'Looks like an existing startup:',
    stagingDetectingLabel:   'Identifying startup…',
    stagedFilesBeforeUnloadMsg: 'You have staged files that will be lost if you leave. Continue?',
};

// ============================================================
// ARTICLE PROVENANCE (Waiting for Review queue)
// WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13
// Maps GET /api/articles/pending's `input_channel` (see
// backend/core/system/constants.py::ARTICLE_INPUT_CHANNELS) to a
// business-facing {intent, label} pair so the CEO can tell at a glance
// which queued rows he submitted himself versus which arrived on their
// own (e.g. the daily Inc42 harvest). `intent` resolves through
// DESIGN_INTENTS via IntentPill — never a raw hex value.
// ============================================================
export const ARTICLE_PROVENANCE = {
    manual_upload:         { intent: 'neutral', label: SHARED_UI_LABELS.articleProvenanceSelf },
    url_fetch:              { intent: 'neutral', label: SHARED_UI_LABELS.articleProvenanceSelf },
    scraper_inc42_indepth:  { intent: 'info',    label: SHARED_UI_LABELS.articleProvenanceAuto },
    email_monitor:          { intent: 'info',    label: SHARED_UI_LABELS.articleProvenanceEmail },
};
// Legacy rows predating the input_channel field, or any future unrecognized
// channel — default to the CEO's own submission, which is what every row
// was before automated intake existed.
export const ARTICLE_PROVENANCE_DEFAULT = { intent: 'neutral', label: SHARED_UI_LABELS.articleProvenanceSelf };

// ============================================================
// LINKEDIN CONTENT TYPE AUTHORITY
// Mirrors backend constants LINKEDIN_POST_CONTENT_TYPE_TEXT / _VIDEO.
// Frontend cannot import Python constants — these are the canonical
// JS equivalents. All chip labels must reference LINKEDIN_CONTENT_TYPE_LABELS;
// never write a bare string literal for content-type values or labels.
// Authority: VIDEO_CARD_TITLE_DESIGN_REVIEW_2026-05-31.md §5 (Approach A).
// ============================================================
export const LINKEDIN_CONTENT_TYPES = {
    TEXT:  'text',
    VIDEO: 'video',
};

export const LINKEDIN_CONTENT_TYPE_LABELS = {
    [LINKEDIN_CONTENT_TYPES.TEXT]:  'Text',
    [LINKEDIN_CONTENT_TYPES.VIDEO]: 'Video',
};

// ============================================================
// REJECTED SUB-TABS — the destination for a decided idea
// WI-ONE-POST-REACHES-THE-CEO-001 Leg 3. CEO ruling D-2026-07-31-15, in full: "sub-tabs are
// permitted under REJECTED only — ideas and posts — AND a decided idea leaves the Ideas tab;
// five tabs otherwise, and no other sub-tabs." Recorded twice in truncated form (the exception
// dropped), which made two windows refuse what he had allowed; corrected in PR #1341.
// ============================================================
// `noun` is what the tablist speaks aloud beside the count ("Ideas, 4 ideas"). Without it
// both sub-tabs would be announced as "posts", and an idea is not a post — that distinction
// is the entire reason the ruling permits two sub-tabs rather than one list.
// `posts` is FIRST on purpose: it is the list that was already there, so a returning reader
// opens the Rejected tab on exactly what it opened on before.
export const REJECTED_SUBTABS = [
    { id: 'posts', label: 'Posts', noun: 'post' },
    { id: 'ideas', label: 'Ideas', noun: 'idea' },
];

export const REJECTED_SUBTAB_ARIA_LABEL = 'Rejected: ideas or posts';

// The Ideas tab's own sub-tab row — CEO ruling D-2026-08-06-1, which supersedes
// D-2026-07-31-15's "no other sub-tabs" clause for this tab only. Named as its own accessible
// label rather than reusing the top-level one: a screen reader announcing "Post Studio views"
// twice on one screen tells the CEO nothing about which row he has landed in.
// The sub-tab NAMES are not declared here on purpose — they come from tab_definitions.py with
// the groups, so the tab can never describe itself differently from the set it is showing.
export const IDEA_SUBTAB_ARIA_LABEL = 'Ideas: waiting on you, held back, or queued';

export const REJECTED_SUBTAB_LABELS = {
    // Says what this list IS, not what it is waiting for — the Ideas tab's own status line
    // ("waiting for a draft") would be false here.
    ideasStatus: 'Ideas that were decided against — yours and the system\'s.',
    ideasEmpty: 'No idea has been decided against yet.',
    ideasFailed:
        'The decided ideas could not be loaded just now, so nothing below is being shown. This is not the same as there being none — refresh to try again.',
};

// ============================================================
// GATE ANNOTATIONS — what a machine check objected to, in the CEO's language
// WI-ONE-POST-REACHES-THE-CEO-001 (Leg 4). The gate ruling of 2026-08-01 — "A machine gate may
// ANNOTATE a draft. It may not kill one. The seat decides." — made the backend write a
// `gate_annotations` entry onto every draft a check fired on. Nothing rendered it, so for the
// one reader it was written for it did not exist.
//
// `failure_class` is a MACHINE string (`wordcount`, `banned_word`, `stance_narration`). CLAUDE.md
// rules 17-18 forbid tech jargon on any CEO-facing surface, so it never reaches the screen — this
// map is the only place it is translated, and the annotation's own `reason` sentence carries the
// detail underneath. An UNMAPPED class deliberately has no fallback label invented for it: the
// component falls back to the generic heading and still shows the reason and the quoted sentence,
// because a check the CEO cannot see is the failure this whole lane was opened to fix.
//
// `intent` is a DESIGN_INTENTS key, so no surface picks a colour. All of these are `warning`, not
// `negative`: the draft SURVIVED the check and is in front of him to decide on. Rendering it red
// would say "rejected", which is exactly the veto the ruling removed.
// ============================================================
export const LINKEDIN_GATE_ANNOTATION_LABELS = {
    wordcount:         { label: 'Length',        intent: 'warning' },
    exclamation:       { label: 'Punctuation',   intent: 'warning' },
    emdash:            { label: 'Punctuation',   intent: 'warning' },
    banned_word:       { label: 'Wording',       intent: 'warning' },
    opening:           { label: 'Opening line',  intent: 'warning' },
    source:            { label: 'Source',        intent: 'warning' },
    ai_score:          { label: 'Quality score', intent: 'warning' },
    gate_unavailable:  { label: 'Check unavailable', intent: 'warning' },
    altitude_retreat:  { label: 'Altitude',      intent: 'warning' },
    closing_scaffold:  { label: 'Closing line',  intent: 'warning' },
    no_horizon:        { label: 'Forward time frame', intent: 'warning' },
    move_missing:      { label: 'Next decision', intent: 'warning' },
    // Retained deliberately. The self-narration JUDGE was switched off on 2026-08-02, but
    // annotations already written under it still have to render, and the switch is revertible.
    stance_narration:  { label: 'Voice',         intent: 'warning' },
};

export const LINKEDIN_GATE_ANNOTATION_LABELS_COPY = {
    heading: 'One of our checks flagged this draft',
    headingPlural: 'Our checks flagged this draft',
    // Says the thing the ruling exists to say, in the CEO's own frame.
    subheading: 'Nothing here stopped the draft. It is yours to decide on.',
    quotedLead: 'The sentence it objected to:',
    // The honest answer when no sentence can be quoted. The backend writes the specific
    // wording per check; this is only the label above it.
    noQuoteLead: 'Why no sentence is quoted:',
};

// ============================================================
// AGENT SUBGROUP LABELS
// Visual labels for sidebar sub-groups within the Agents section.
// AppLayout reads this to render group headers above the first item of each group.
// ============================================================
export const AGENT_SUBGROUPS = {
    sourcing:       { label: 'Sourcing' },
    relationships:  { label: 'Relationships' },
    content:        { label: 'Content' },
    analysis:       { label: 'Analysis' },
};

// ============================================================
// COCKPIT LABELS — WI-UI-CLEANUP-001 Stage 2
// All user-visible strings rendered by the three-pane Inbox cockpit
// (CockpitLayout, TakeawayBar, SummaryTiles, DealListPane,
// DealPreviewPane, DealInspectorPane). Import from here so cockpit
// strings are localizable and grep-friendly. Never hardcode in JSX.
// Authority: docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §16.
// ============================================================
export const COCKPIT_LABELS = {
    aria: {
        cockpit:   'Inbox cockpit',
        list:      'Inbox deals',
        preview:   'Deal preview',
        inspector: 'Deal inspector',
    },
    listGroups: {
        worthACall: 'Worth a call',
        inReview:   'In review',
        passes:     'Passes',
    },
    taxonomy: {
        sector:        'Sector',
        // Law §8.4: a pane with no `sector_state` from the backend says NOT CHECKED, never the screen's own sector text.
        sectorNotChecked: AGENT_CONFIG.deal_evaluator.sectorStateNotCheckedCompactLabel,
        businessModel: 'Business Model',
        customerType:  'Customer Type',
        gtmMotion:     'GTM Motion',
        pendingFootnote: 'Registry update pending for some taxonomy fields.',
    },
    inspectorActions: {
        sendToFCA:           'Send to FCA',
        passWithNote:        'Pass with note',
        // Replaces passWithNote on the deal inspector as of 2026-08-06: the control offers a
        // list, not a note, and calling it a note invited the free text the taxonomy forbids.
        // passWithNote is left declared — other surfaces may still reference it.
        passWithReason:      'Pass with reason',
        scheduleFounderCall: 'Schedule founder call',
        park:                'Park',
        comingNextWi:        'Coming in next WI',
        passReasonPlaceholder: 'Choose a reason…', // noscan — placeholder copy
        passReasonAriaLabel: 'Reason for passing on this deal', // noscan — accessible name
        // Shown INSTEAD of the picker when the vocabulary cannot be read. A classification
        // field with no list refuses; it never falls back to a free-text box.
        passReasonListMissing:
            'The list of pass reasons could not be loaded, so this deal cannot be passed right now. Nothing has been recorded.', // noscan — refusal copy
        passConfirm:         'Confirm',
        passCancel:          'Cancel',
        openInspector:       'View details',
    },
    overlay: {
        back: 'Back',
    },
    tiles: {
        strong:      'Strong',
        weak:        'Weak',
        pass:        'Pass',
        directional: 'Directional',
        oldest:      'Oldest',
        overnight:   'Overnight',
        kbWrites:    'KB Writes',
        // Shown above the tiles when the figures could not be loaded, so an
        // unreachable summary can never be read as "everything is zero".
        unavailable: "These figures couldn't be loaded just now — the counts below are unknown, not zero. Refresh to try again.",
    },
    // WI-INBOX-ROW-CONTENT-001: agent label shown on the top line of every inbox list row.
    // Keys mirror WorkItem.work_type values from backend/routes/work_inbox.py.
    agentLabels: {
        tracxn_livedeals:   'Deal Sourcing',
        first_look:         'Deal Sourcing',
        deal_screen:        'Deal Evaluator',
        review_pitch_deck:  'FCA',
        article_review:     'Sector Intelligence',
        linkedin_post:      'Post Studio',
        lead_gen_scout:     'LinkedIn Scout',
        kb:                 'Knowledge Base',
        review:             'Review',
        startup:            'Startup',
        campaign:           'Founder Outreach',
        principle_promotion:'Principle Library',
        startup_review:     'Sector Intelligence',
    },
    // WI-INFRA-UPGRADE-CLOSEOUT-BUNDLE-001 Leg D. StartupReviewDetail.jsx
    // candidate field labels — mirrors the six SIA canonical fields
    // (junior_agent.py CLASSIFICATION_FIELDS). Business-facing, no raw field
    // names rendered to the CEO.
    startupReviewFieldLabels: {
        sector:          'Sector',
        business_model:  'Business Model',
        customer_type:   'Customer Type',
        target_vertical: 'Target Vertical',
        gtm_motion:      'GTM Motion',
        stage:           'Stage',
    },
    // WI-INBOX-ROW-CONTENT-001: single authority for LinkedIn post_type display strings.
    // Mirrors LinkedInPostDetail.jsx POST_TYPE_LABELS so detail renderer + list row agree.
    postTypeLabels: {
        thesis_confirmed:   'Thesis Confirmed',
        thesis_challenged:  'Thesis Challenged',
        standalone_insight: 'Standalone Insight',
        emerging_pattern:   'Emerging Pattern',
        contrarian_read:    'Contrarian Read',
    },
    // WI-INBOX-ROW-CONTENT-001: signal_strength label mapping for inbox row tags.
    signalLabels: {
        strong_signal: SHARED_UI_LABELS.strongSignal,
        signal:        'Signal',
        noise:         'Noise',
    },
    // WI-INBOX-ROW-CONTENT-001: separator used to join row-tag items on the bottom line.
    rowSeparator: ' · ',
    // WI-INBOX-ROW-CONTENT-001: Scout row tag templates — business-facing phrasing only.
    scoutTagFormat: {
        candidatesSuffix: ' candidates',
        cohortPrefix:     'Cohort ',
    },
    emptyState: {
        // Preview pane when nothing is selected.
        preview:          'Select an item from the list',
        // Inspector pane when the selected item is not a deal-type (tracxn_livedeals / first_look / deal_screen).
        // Non-deal types render their details inline in the preview pane.
        inspectorNonDeal: 'Details available in preview',
        // Shown when a card cannot be rendered. The pane must SAY SO rather
        // than leave whatever was on screen before — see PreviewErrorBoundary.
        previewFailedTitle: 'This card could not be opened',
        previewFailedBody:
            'Something went wrong showing this one. Nothing has been changed, and the rest of your inbox still works — pick another card, or refresh and try again.',
    },
};

// ─── Idea queue (the Ideas tab of Post Studio) ────────────────────────────────
//
// The CEO's own queue of ideas he has decided on that are still waiting for a
// draft. Every sentence here is written to keep two things apart that used to
// look identical to him: "nothing is waiting" and "we could not look".
//
// Consumed by frontend/src/components/post-studio/IdeaTabPanel.jsx. It was a
// standalone page at /ideas until 2026-07-30, when the surface moved into the first
// tab of Post Studio and that route was removed
// (WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001).

export const IDEA_QUEUE_LABELS = {
    title: 'Ideas waiting for a draft',
    loading: 'Looking…',
    // A load failure is stated outright — an empty table would read as an empty
    // queue, which is the lie this page exists to avoid.
    failed:
        'Your idea queue could not be loaded just now, so nothing below is being shown. This is not the same as having no ideas waiting — refresh to try again.',
    emptyAll: 'There are no ideas in the queue at all — nothing has been triggered and left undecided.',
    noneWaiting: 'Nothing you have approved is waiting for a draft right now.',
    colClaim: 'The idea',
    colStatus: 'Status',
    colAge: 'Waiting',
    colResearch: 'Research saved',
    awaiting: 'Approved — waiting for a draft',
    undecided: 'Waiting on your decision',

    // ── The card's ACTUAL recorded decision (WI-IDEA-RERUN-AND-ACTIONABLE-IDEA-TAB-001)
    // The status column used to read `awaiting_draft ? awaiting : undecided`, so anything not
    // advancing read "Waiting on your decision" — including a card the CEO had already
    // REJECTED. He reads this surface by text-to-speech, so it told him aloud that a decided
    // card still needed deciding. These render the real idea_card.card_status from the closed
    // contract vocabulary (pending | approved | rejected | promoted); no new state is invented
    // and nothing is inferred.
    statusPending:  'Waiting on your decision',
    statusApproved: 'You approved this — waiting for a draft',
    statusPromoted: 'You promoted this — waiting for a draft',
    statusRejected: 'You rejected this',
    // THE FIFTH STATE (WI-PROMOTE-THE-HEADLINE-GUARD-AND-CLEAR-THE-PRE-RULE-CARDS-001 Leg 2, CEO ruling
    // 2026-07-31). Deliberately its own sentence, never folded into statusRejected — "You
    // rejected this" would tell him aloud that he made a decision he never made. This is the
    // opposite: the system pulled the card back on its own, before it ever reached him.
    statusSystemRefused: 'The system pulled this back on its own — not a decision you made',
    // An absent card_status is a PRODUCER GAP, not a state (IDEA_CARD_CONTRACT §6) — it must
    // never be shown as though the CEO had left it undecided.
    statusMissing:  'This card records no decision state, which is a fault in how it was created rather than a decision you have not made',

    // ── Decision controls. Only the actions the store can already record.
    // ── Drafts withheld from the Pending tab (WI-IDEA-RERUN-AND-ACTIONABLE-IDEA-TAB-001)
    // A finished draft can be archived, which reversibly drops it out of Pending. That is a
    // real feature, but the effect was total silence: one draft — 1,502 characters of finished
    // text, written 2026-07-10, archived 2026-07-13 — had never appeared on any screen, and the
    // tab read 0 while the store held it. An honest 0 about what is PENDING is still a silence
    // about what is WITHHELD. These say what is being held back and why, without promoting it.
    withheldHeading: (n) =>
        n === 1
            ? '1 finished draft is being held back from this tab'
            : `${n} finished drafts are being held back from this tab`,
    // The reason a draft was archived, when one is recorded on it (23 Oct 2026): the line above says what archiving IS; this says why THIS draft.
    withheldReasonLead: 'Why this one:',
    withheldWhy: 'Archived, which removes a draft from this tab without deleting it. Nothing has been lost — it is still on record and can be brought back.',
    // PILL INTENTS (WI-ONE-CARD-EVERY-TAB-COPY-SAVED-AND-DELETE-THE-COPIES-001).
    // The Ideas and withheld cards render the SAME pill row the Saved card does, so which
    // intent each fact carries is a declared choice here rather than a colour picked inside a
    // component. Keys are DESIGN_INTENTS keys. Every pill also carries its own WORDS — colour
    // never signals alone, because this surface is read by text-to-speech.
    pillIntents: {
        source: 'info',
        age: 'neutral',
        researchYes: 'positive',
        researchNo: 'warning',
        statusApproved: 'positive',
        statusPromoted: 'positive',
        statusRejected: 'negative',
        // 'neutral', not 'negative' — this is not a CEO rejection and must not carry the same
        // colour signal as one, even though the row also carries its own words (WI-IDEA-CARD-
        // FULL-READ-REFUSED-STATE-AND-ALARM-001 Leg 2).
        statusSystemRefused: 'neutral',
        statusPending: 'warning',
        statusMissing: 'neutral',
        withheldArchived: 'warning',
        withheldOn: 'neutral',
        withheldChars: 'info',
    },
    withheldOn: 'Archived on',
    withheldChars: (n) => `${n.toLocaleString()} characters of finished text`,
    // The draft body opens on request rather than being dumped inline. Every sibling tab
    // collapses a long post body behind a control; Pending was the one surface rendering
    // 1,502 characters of finished text in full, which is what made it read as a different
    // screen. The control carries its own WORD, because the surface is read by ear.
    // WI-POST-STUDIO-VERIFY-LIVE-AND-REVERT-PENDING-BY-DIFF-001.
    withheldReadFull: 'Read the full draft',
    withheldHideFull: 'Hide the full draft',

    colDecide:      'Your decision',
    actApprove:     'Approve',
    actPromote:     'Promote',
    actReject:      'Reject',
    actBusy:        'Recording…',
    // The reject menu's own heading. Declared here rather than typed into the component,
    // because the menu now renders on TWO surfaces (the Work Inbox card and the Post Studio
    // Ideas tab) and a string typed into one of them is how the two drifted apart before
    // (WI-IDEA-LANE-EXECUTE-CEO-RULINGS-001, item B).
    rejectMenuHeading: 'Why is this idea not worth writing?',
    // A rejected COMMENT card uses the same menu with its own heading (addendum 1, 30 Oct 2026).
    commentRejectMenuHeading: 'Why was this comment not right?',
    rejectPrompt:      'Why are you rejecting this idea?',
    actCancel:         'Cancel',
    decideFailed:   'That decision could not be recorded, so nothing has changed. The reason is shown above.',
    decidedAlready: 'Already decided',
    repairHeading: 'This card could not be finished',
    // An approved idea that left the queue without a draft: what became of it (26 Oct 2026; the sentence itself comes from the server).
    outcomeHeading: 'What became of it',
    statusApprovedNoDraft: 'You approved this — no draft came out of it',
    leftQueueFailed:
        'The ideas you approved that left the queue could not be loaded just now. This is not the same as having none — refresh to try again.',
    repairMissing: 'What held it back',
    repairStopped: 'We stopped trying automatically after {attempts} tries.',
    actRepair: 'Try again',
    repairBusy: 'Trying again...',
    repairDone: 'Rebuilt. It moves to Waiting on you once no check objects.',
    repairStill: 'It still could not be finished. The reason above is the latest.',
    repairFailed: 'We could not try again just now.',
    actCommission: 'Ask for a different idea from this article',
    commissionBusy: 'Asking...',
    commissionDone: 'A different idea from this article is now waiting for your decision on the Ideas tab.',
    commissionWaiting: 'A different idea from this article is already waiting for your decision.',
    commissionFailed: 'No different idea could be made from this article.',
    // All-or-nothing, exactly as drafting decides it (CEO ruling).
    researchYes: 'Yes — drafting will reuse it and buy nothing',
    researchNo: 'No — drafting will buy it',
    ageUnknown: 'unknown',
    fromArticle: 'From:',

    // ── Where each idea's claim came from (WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001)
    // The claim is seeded once at birth, so the cards minted before the 2026-07-30 caller
    // repair carry the article headline and the ones minted after it carry the firm's own
    // written read. Both are shown on this surface together, and every row says in PLAIN
    // WORDS which kind it is — the CEO reads this screen by text-to-speech, so a colour or
    // an icon would not reach him.
    //
    // These are descriptions, never verdicts. A card whose claim is its headline is FROZEN,
    // not wrong: the claim cannot be recomputed after birth, and what happens to those cards
    // is the CEO's separate ruling. Nothing here may read as broken, invalid or superseded.
    colSource: 'Where the idea came from',
    sourceOurAnalysis: 'From our own written read of the article',
    sourceHeadline: 'The article headline, used in place of a claim',
    sourceCounterArgument: "From the article's counter-argument",
    // The same sentence the Work Inbox card shows for a composed claim (LinkedInPostDetail.buildClaimSourceLine), so the
    // two surfaces say one thing. A composed claim is written from the assembled material, not lifted from a stored text.
    sourceComposed: (unknown) => `Claim composed from the assembled material${unknown ? ` (${unknown} of 5 tests: not enough material)` : ''}`,
    sourceUndetermined: 'Cannot be determined from what this card stored',
    // ABSENCE IS NOT AN EMPTY ANSWER. Two different things were rendering as one sentence:
    // "the card's stored texts do not match its claim" (a real answer about the card) and
    // "the server did not report a claim source at all" (we could not look). Observed live on
    // 2026-07-30 when the frontend deployed ahead of the backend — every row read
    // "Cannot be determined from what this card stored", which was not true of the cards.
    // Same shape as a count that reads 0 because nobody answered.
    sourceNotReported: 'Where this claim came from was not reported — this is not the same as the card having no source',
    // Suffix on the good path only, so the CEO can see how much work is behind the claim.
    sourceAnalysisChars: (n) => `${n.toLocaleString()} characters of analysis behind it`,
};
